/**

 * Stice (flat house) wall/arc geometry for Site Layout — bin-edge snap points → layout sides (feet).

 * Walls and arcs connect snap points on placed round-bin markers, not freeform outlines.

 */



export const DEFAULT_MARKER_RADIUS_PX = 28;

/** Typical round-bin diameter (ft) when BTS has no size — matches Site 3D layout fallback. */
export const FALLBACK_LAYOUT_BIN_DIAMETER_FT = 30;

/** BTS/catalog diameter (ft) for a site bin record. */
export const catalogBinDiameterFt = (bin) => {
    if (!bin) return 0;
    const d = Number(bin.diameter);
    if (d > 0) return d;
    const w = Number(bin.width);
    const l = Number(bin.length);
    if (w > 0 && l > 0) return Math.max(w, l);
    return 0;
};

/**
 * Default editor ft/px: one default marker circle (DEFAULT_MARKER_RADIUS_PX) = typical site bin diameter.
 * Site 3D layout positions/sizes are in feet; this aligns canvas spacing with saved layout tables.
 */
export const defaultLayoutFeetPerPixel = (siteBins = []) => {
    const diameters = (siteBins || [])
        .map(catalogBinDiameterFt)
        .filter((d) => d > 0)
        .sort((a, b) => a - b);
    const refD =
        diameters.length > 0
            ? diameters[Math.floor(diameters.length / 2)]
            : FALLBACK_LAYOUT_BIN_DIAMETER_FT;
    return refD / (2 * DEFAULT_MARKER_RADIUS_PX);
};

export const PIXEL_TO_FEET_DEFAULT = defaultLayoutFeetPerPixel([]);



export const newSticeId = () =>

    `stice_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;



export const newSegmentId = () =>

    `seg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;



export const getMarkerRadiusImagePx = (marker) => {

    if (marker?.radiusPx > 4 && Number.isFinite(marker.radiusPx)) {

        return marker.radiusPx;

    }

    return DEFAULT_MARKER_RADIUS_PX;

};



export const BIN_SNAP_COUNT = 16;
export const BIN_SNAP_STEP = Math.PI / 8;

/** Nearest bin snap angle (0°, 22.5°, … 337.5°). */
export const quantizeBinSnapAngle = (angle) => {
    const na = normalizeAngle(angle);
    let best = 0;
    let bestGap = Infinity;
    for (let k = 0; k < BIN_SNAP_COUNT; k++) {
        const a = k * BIN_SNAP_STEP;
        let gap = Math.abs(normalizeAngle(na - a));
        if (gap > Math.PI) gap = 2 * Math.PI - gap;
        if (gap < bestGap) {
            bestGap = gap;
            best = a;
        }
    }
    return best;
};

/** Snap targets on each round bin: 16 points at 22.5° increments on the perimeter. */
export const collectBinSnapPoints = (markers) => {
    const points = [];
    if (!markers?.length) return points;

    markers.forEach((m) => {
        const r = getMarkerRadiusImagePx(m);
        for (let k = 0; k < BIN_SNAP_COUNT; k++) {
            const angle = k * BIN_SNAP_STEP;
            points.push({
                markerId: m.id,
                angle,
                x: m.x + r * Math.cos(angle),
                y: m.y + r * Math.sin(angle),
                kind: "cardinal",
            });
        }
    });

    return dedupeSnapPoints(points);
};



/** Fractions along a wall segment that expose snap targets (thirds, quarters, midpoint). */
const WALL_SEGMENT_SNAP_FRACTIONS = [1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4];

const wallPointAtFraction = (g, t) => ({
    x: g.x1 + t * (g.x2 - g.x1),
    y: g.y1 + t * (g.y2 - g.y1),
});

const dedupeSnapPoints = (points) => {
    const out = [];
    points.forEach((p) => {
        const dup = out.find((q) => {
            if (Math.hypot(q.x - p.x, q.y - p.y) >= 2) return false;
            if (p.kind === "segment-end" && q.kind === "segment-end") {
                return p.segmentId === q.segmentId && p.end === q.end;
            }
            if (p.kind === "segment-mid" && q.kind === "segment-mid") {
                return (
                    p.segmentId === q.segmentId &&
                    (p.t ?? 0.5) === (q.t ?? 0.5)
                );
            }
            return (
                p.markerId === q.markerId &&
                p.angle != null &&
                q.angle != null &&
                Math.abs(p.angle - q.angle) < 0.04
            );
        });
        if (!dup) out.push(p);
    });
    return out;
};



const snapKindPriority = (kind) => {
    if (kind === "segment-mid") return 0;
    if (kind === "segment-end") return 1;
    if (kind === "cardinal") return 2;
    return 4;
};

const nearestSnap = (imageX, imageY, snaps, tolImage) => {
    let best = null;
    let bestDist = Infinity;
    let bestPri = Infinity;
    snaps.forEach((sp) => {
        const d = Math.hypot(imageX - sp.x, imageY - sp.y);
        if (d > tolImage) return;
        const pri = snapKindPriority(sp.kind);
        if (pri < bestPri || (pri === bestPri && d < bestDist)) {
            bestPri = pri;
            bestDist = d;
            best = sp;
        }
    });
    return best;
};

const normalizeAngle = (a) => {
    let x = a;
    while (x <= -Math.PI) x += 2 * Math.PI;
    while (x > Math.PI) x -= 2 * Math.PI;
    return x;
};

export const resolveEndpoint = (seg, end, markers, segments, visiting) => {
    const attach = seg[`attach${end}`];
    if (attach?.segmentId) {
        const parent = (segments || []).find((s) => s.id === attach.segmentId);
        if (!parent) return null;
        const g = resolveSegmentGeometry(parent, markers, segments, visiting);
        if (!g) {
            if (attach.x != null && attach.y != null) {
                return { x: attach.x, y: attach.y };
            }
            return null;
        }
        if (attach.role === "mid") {
            if (g.kind === "arc" && g.curveX != null && g.curveY != null) {
                return { x: g.curveX, y: g.curveY };
            }
            const t = attach.t ?? 0.5;
            return wallPointAtFraction(g, t);
        }
        if (attach.role === "start") return { x: g.x1, y: g.y1, markerId: g.markerId1, angle: g.angle1 };
        return { x: g.x2, y: g.y2, markerId: g.markerId2, angle: g.angle2 };
    }
    const markerId = seg[`markerId${end}`];
    const angle = seg[`angle${end}`];
    if (markerId == null || angle == null) return null;
    const m = markers.find((mk) => mk.id === markerId);
    if (!m) return null;
    const r = getMarkerRadiusImagePx(m);
    return {
        x: m.x + r * Math.cos(angle),
        y: m.y + r * Math.sin(angle),
        markerId,
        angle,
    };
};

/** Resolve segment endpoints (bin edge, wall endpoint, or wall midpoint attach). */
export const resolveSegmentGeometry = (seg, markers, segments = [], visiting = null) => {
    if (!seg) return null;

    if (
        isClosedRectSegment(seg) &&
        seg.x1 != null &&
        seg.y1 != null &&
        seg.x2 != null &&
        seg.y2 != null
    ) {
        return {
            id: seg.id,
            kind: seg.kind || "wall",
            x1: seg.x1,
            y1: seg.y1,
            x2: seg.x2,
            y2: seg.y2,
            isInner: !!seg.isInner,
        };
    }

    const hasMarkers = markers?.length > 0;
    const visit = visiting || new Set();
    if (visit.has(seg.id)) return null;
    visit.add(seg.id);

    const p1 = hasMarkers ? resolveEndpoint(seg, 1, markers, segments, visit) : null;
    const p2 = hasMarkers ? resolveEndpoint(seg, 2, markers, segments, visit) : null;
    visit.delete(seg.id);

    if (p1 && p2) {
        const geom = {
            id: seg.id,
            kind: seg.kind,
            markerId1: p1.markerId ?? null,
            angle1: p1.angle ?? null,
            markerId2: p2.markerId ?? null,
            angle2: p2.angle ?? null,
            x1: p1.x,
            y1: p1.y,
            x2: p2.x,
            y2: p2.y,
            isInner: !!seg.isInner,
        };
        if (seg.kind === "arc" && seg.bulgeX != null && seg.bulgeY != null) {
            geom.curveX = seg.bulgeX;
            geom.curveY = seg.bulgeY;
        } else if (seg.kind === "arc" && seg.curveX != null && seg.curveY != null) {
            geom.curveX = seg.curveX;
            geom.curveY = seg.curveY;
        } else if (seg.kind === "arc" && seg.bulgeMarkerId != null && seg.bulgeAngle != null) {
            const mb = markers.find((m) => m.id === seg.bulgeMarkerId);
            if (mb) {
                const rb = getMarkerRadiusImagePx(mb);
                geom.curveX = mb.x + rb * Math.cos(seg.bulgeAngle);
                geom.curveY = mb.y + rb * Math.sin(seg.bulgeAngle);
            }
        }
        return geom;
    }

    if (segmentIsFullySnapped(seg)) return null;

    if (seg.x1 != null && seg.x2 != null) {
        return {
            id: seg.id,
            kind: seg.kind || "wall",
            x1: seg.x1,
            y1: seg.y1,
            x2: seg.x2,
            y2: seg.y2,
            curveX: seg.curveX,
            curveY: seg.curveY,
            isInner: !!seg.isInner,
        };
    }
    return null;
};

/** Snap targets on wall/arc endpoints and wall quarter/mid points (for chaining segments). */
export const collectSegmentSnapPoints = (markers, segments) => {
    const points = [];
    (segments || []).forEach((raw) => {
        const g = resolveSegmentGeometry(raw, markers, segments);
        if (!g) return;
        if (g.kind !== "wall" && g.kind !== "arc") return;

        points.push({
            kind: "segment-end",
            segmentId: raw.id,
            end: "start",
            x: g.x1,
            y: g.y1,
        });
        points.push({
            kind: "segment-end",
            segmentId: raw.id,
            end: "end",
            x: g.x2,
            y: g.y2,
        });

        if (g.kind === "wall") {
            WALL_SEGMENT_SNAP_FRACTIONS.forEach((t) => {
                const pt = wallPointAtFraction(g, t);
                points.push({
                    kind: "segment-mid",
                    segmentId: raw.id,
                    t,
                    x: pt.x,
                    y: pt.y,
                });
            });
        } else if (g.curveX != null && g.curveY != null) {
            points.push({
                kind: "segment-mid",
                segmentId: raw.id,
                x: g.curveX,
                y: g.curveY,
            });
        }
    });
    return dedupeSnapPoints(points);
};

export const collectLayoutSnapPoints = (markers, segments) => {
    const binSnaps = collectBinSnapPoints(markers).map((sp) => ({
        ...sp,
        kind: sp.kind || "bin",
    }));
    return dedupeSnapPoints([...binSnaps, ...collectSegmentSnapPoints(markers, segments)]);
};

/** Snap to bin edge, wall endpoint, or wall midpoint. */
export const snapImageToLayoutPoint = (
    imageX,
    imageY,
    markers,
    segments,
    canvasScaleMin,
    tolCanvasPx = 22
) => {
    const snaps = collectLayoutSnapPoints(markers, segments);
    if (!snaps.length) return null;
    const tolImage = tolCanvasPx / Math.max(canvasScaleMin, 0.001);
    return nearestSnap(imageX, imageY, snaps, tolImage);
};

/** @deprecated use snapImageToLayoutPoint */
export const snapImageToBinPoint = (imageX, imageY, markers, canvasScaleMin, tolCanvasPx = 22) =>
    snapImageToLayoutPoint(imageX, imageY, markers, [], canvasScaleMin, tolCanvasPx);

/**
 * Snap a bin center so one of its 8 perimeter snap points meets a snap point on another bin.
 * Only cardinal bin snap points are used (no tangency, no wall snaps).
 */
export const snapBinCenterToBinSnapPoints = (
    movingId,
    centerX,
    centerY,
    markers,
    tolImagePx = 32
) => {
    const moving = movingId ? markers.find((m) => m.id === movingId) : null;
    const r2 = getMarkerRadiusImagePx(moving || {});
    const targets = collectBinSnapPoints(markers).filter(
        (sp) => !movingId || sp.markerId !== movingId
    );
    if (!targets.length) return { x: centerX, y: centerY };

    let best = { x: centerX, y: centerY };
    let bestDist = tolImagePx;

    targets.forEach((target) => {
        for (let k = 0; k < BIN_SNAP_COUNT; k++) {
            const beta = k * BIN_SNAP_STEP;
            const cx = target.x - r2 * Math.cos(beta);
            const cy = target.y - r2 * Math.sin(beta);
            const d = Math.hypot(cx - centerX, cy - centerY);
            if (d < bestDist) {
                bestDist = d;
                best = { x: cx, y: cy };
            }
        }
    });

    return best;
};

/** Bin-mode center snap with canvas-pixel tolerance (image tolerance scales with zoom). */
export const snapBinCenterForCanvas = (
    movingId,
    centerX,
    centerY,
    markers,
    canvasScaleMin,
    tolCanvasPx = 22
) => {
    const tolImage = tolCanvasPx / Math.max(canvasScaleMin, 0.001);
    return snapBinCenterToBinSnapPoints(movingId, centerX, centerY, markers, tolImage);
};

/** True when this end is anchored to a bin edge or another segment snap. */
export const segmentEndIsSnapped = (seg, end) => {
    const attach = seg[`attach${end}`];
    if (attach?.segmentId) return true;
    return seg[`markerId${end}`] != null && seg[`angle${end}`] != null;
};

/** Closed rectangle flat-house perimeter (four walls, no bin snaps). */
export const isClosedRectSegment = (seg) => !!seg?.closedRectId;

/** Both ends must be on snap points (not free-floating coordinates). */
export const segmentIsFullySnapped = (seg) =>
    isClosedRectSegment(seg) ||
    (segmentEndIsSnapped(seg, 1) && segmentEndIsSnapped(seg, 2));

const MIN_FLAT_RECT_IMAGE_PX = 6;

/** Image-space bounds of a placed flat rectangle. */
export const getFlatRectBounds = (segments, rectId) => {
    const segs = segments.filter((s) => s.closedRectId === rectId);
    if (!segs.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    segs.forEach((s) => {
        if (s.x1 == null) return;
        minX = Math.min(minX, s.x1, s.x2);
        minY = Math.min(minY, s.y1, s.y2);
        maxX = Math.max(maxX, s.x1, s.x2);
        maxY = Math.max(maxY, s.y1, s.y2);
    });
    if (!Number.isFinite(minX)) return null;
    return {
        minX,
        minY,
        maxX,
        maxY,
        segmentIds: segs.map((s) => s.id),
    };
};

/** When an enclosed face is a flat rectangle, return its shared closedRectId. */
export const closedRectIdForFace = (face, segments) => {
    const ids = face?.segmentIds;
    if (!ids?.length || !Array.isArray(segments) || !segments.length) return null;
    let rectId = null;
    for (const sid of ids) {
        const s = segments.find((seg) => seg.id === sid);
        if (!s?.closedRectId) return null;
        if (rectId != null && rectId !== s.closedRectId) return null;
        rectId = s.closedRectId;
    }
    return rectId;
};

export const pointInFlatRectBounds = (x, y, bounds, pad = 0) =>
    x >= bounds.minX - pad &&
    x <= bounds.maxX + pad &&
    y >= bounds.minY - pad &&
    y <= bounds.maxY + pad;

/** Hit-test placed flat houses using live wall geometry (not stale face polygons). */
export const findFlatRectAtPoint = (layoutSegments, x, y, pad = 0) => {
    const seen = new Set();
    let best = null;
    let bestArea = Infinity;
    (layoutSegments || []).forEach((seg) => {
        const rectId = seg.closedRectId;
        if (!rectId || seen.has(rectId)) return;
        seen.add(rectId);
        const bounds = getFlatRectBounds(layoutSegments, rectId);
        if (!bounds || !pointInFlatRectBounds(x, y, bounds, pad)) return;
        const area = (bounds.maxX - bounds.minX) * (bounds.maxY - bounds.minY);
        if (area < bestArea) {
            bestArea = area;
            best = rectId;
        }
    });
    return best;
};

export const resizeFlatBoundsByCorner = (bounds, corner, x, y) => {
    let { minX, minY, maxX, maxY } = bounds;
    switch (corner) {
        case "nw":
            minX = x;
            minY = y;
            break;
        case "ne":
            maxX = x;
            minY = y;
            break;
        case "se":
            maxX = x;
            maxY = y;
            break;
        case "sw":
            minX = x;
            maxY = y;
            break;
        default:
            break;
    }
    if (minX > maxX) [minX, maxX] = [maxX, minX];
    if (minY > maxY) [minY, maxY] = [maxY, minY];
    const tooSmall =
        maxX - minX < MIN_FLAT_RECT_IMAGE_PX || maxY - minY < MIN_FLAT_RECT_IMAGE_PX;
    return { minX, minY, maxX, maxY, tooSmall };
};

/** Corner grab band in image px (corner quadrants only — interior is move). */
export const FLAT_RECT_CORNER_HIT_TOL_IMAGE = 10;

/** Canvas px tolerance for corner handles (matches on-canvas square grab size). */
export const FLAT_RECT_CORNER_HIT_TOL_CANVAS = 24;

/** Corner grab band in image px — only the corner quadrants, not the flat interior. */
export const findFlatRectCornerQuadrant = (
    imageX,
    imageY,
    bounds,
    tol = FLAT_RECT_CORNER_HIT_TOL_IMAGE
) => {
    const { minX, minY, maxX, maxY } = bounds;
    const w = Math.max(maxX - minX, MIN_FLAT_RECT_IMAGE_PX);
    const h = Math.max(maxY - minY, MIN_FLAT_RECT_IMAGE_PX);
    const bandX = Math.min(tol, w * 0.38);
    const bandY = Math.min(tol, h * 0.38);
    const candidates = [];
    if (imageX <= minX + bandX && imageY <= minY + bandY) {
        candidates.push({
            id: "nw",
            d: Math.hypot(imageX - minX, imageY - minY),
        });
    }
    if (imageX >= maxX - bandX && imageY <= minY + bandY) {
        candidates.push({
            id: "ne",
            d: Math.hypot(imageX - maxX, imageY - minY),
        });
    }
    if (imageX >= maxX - bandX && imageY >= maxY - bandY) {
        candidates.push({
            id: "se",
            d: Math.hypot(imageX - maxX, imageY - maxY),
        });
    }
    if (imageX <= minX + bandX && imageY >= maxY - bandY) {
        candidates.push({
            id: "sw",
            d: Math.hypot(imageX - minX, imageY - maxY),
        });
    }
    if (!candidates.length) return null;
    candidates.sort((a, b) => a.d - b.d);
    return candidates[0].id;
};

/** Canvas px hit radius for the center move handle (interior drag). */
export const FLAT_RECT_MOVE_HANDLE_HIT_CANVAS = 20;

/** Move-handle anchor below the area name (bottom-center inset, image px). */
export const flatRectMoveHandleImagePoint = (bounds) => {
    const w = Math.max(bounds.maxX - bounds.minX, MIN_FLAT_RECT_IMAGE_PX);
    const h = Math.max(bounds.maxY - bounds.minY, MIN_FLAT_RECT_IMAGE_PX);
    const inset = Math.min(16, h * 0.14, w * 0.14);
    return {
        x: (bounds.minX + bounds.maxX) / 2,
        y: bounds.maxY - inset,
    };
};

/** Move handle grab target on a flat (image coords). */
export const findFlatRectMoveCenterHit = (
    layoutSegments,
    imageX,
    imageY,
    scaleX,
    scaleY,
    restrictToRectId = null
) => {
    const scaleMin = Math.min(scaleX, scaleY, 1e-6);
    const tol = Math.max(
        8,
        FLAT_RECT_MOVE_HANDLE_HIT_CANVAS / scaleMin
    );
    const seen = new Set();
    let best = null;
    let bestD = tol;
    (layoutSegments || []).forEach((seg) => {
        const rectId = seg.closedRectId;
        if (!rectId || seen.has(rectId)) return;
        if (restrictToRectId != null && rectId !== restrictToRectId) return;
        seen.add(rectId);
        const bounds = getFlatRectBounds(layoutSegments, rectId);
        if (!bounds) return;
        const { x: cx, y: cy } = flatRectMoveHandleImagePoint(bounds);
        const d = Math.hypot(imageX - cx, imageY - cy);
        if (d <= bestD) {
            bestD = d;
            best = { rectId, bounds };
        }
    });
    return best;
};

/** Nearest corner handle (image px) for resize — nw, ne, se, sw. */
export const findFlatRectCornerAtImage = (
    imageX,
    imageY,
    bounds,
    tol = FLAT_RECT_CORNER_HIT_TOL_IMAGE
) => findFlatRectCornerQuadrant(imageX, imageY, bounds, tol);

/** Nearest corner handle (canvas px) — prefer findFlatRectCornerAtImage. */
export const findFlatRectCornerAtCanvas = (
    canvasX,
    canvasY,
    bounds,
    scaleX,
    scaleY,
    tol = FLAT_RECT_CORNER_HIT_TOL_CANVAS
) => {
    const scaleMin = Math.min(scaleX, scaleY, 1e-6);
    const tolImage = Math.max(
        FLAT_RECT_CORNER_HIT_TOL_IMAGE,
        tol / scaleMin
    );
    const imageX = canvasX / scaleX;
    const imageY = canvasY / scaleY;
    return findFlatRectCornerAtImage(imageX, imageY, bounds, tolImage);
};

/** Corner anchor under cursor — checks all rectangle flats (image px). */
export const findFlatRectCornerHit = (
    layoutSegments,
    imageX,
    imageY,
    scaleX,
    scaleY,
    tolImage = FLAT_RECT_CORNER_HIT_TOL_IMAGE,
    restrictToRectId = null
) => {
    const scaleMin = Math.min(scaleX, scaleY, 1e-6);
    const tol = Math.max(
        tolImage,
        FLAT_RECT_CORNER_HIT_TOL_CANVAS / scaleMin
    );
    const seen = new Set();
    let best = null;
    let bestDist = tol;
    (layoutSegments || []).forEach((seg) => {
        const rectId = seg.closedRectId;
        if (!rectId || seen.has(rectId)) return;
        if (restrictToRectId != null && rectId !== restrictToRectId) return;
        seen.add(rectId);
        const bounds = getFlatRectBounds(layoutSegments, rectId);
        if (!bounds) return;
        const corner = findFlatRectCornerQuadrant(imageX, imageY, bounds, tol);
        if (!corner) return;
        const cx =
            corner === "nw" || corner === "sw" ? bounds.minX : bounds.maxX;
        const cy =
            corner === "nw" || corner === "ne" ? bounds.minY : bounds.maxY;
        const d = Math.hypot(imageX - cx, imageY - cy);
        if (d <= bestDist) {
            bestDist = d;
            best = { rectId, corner, bounds };
        }
    });
    return best;
};

const mergeNearPoints = (points, eps = 0.75) => {
    const out = [];
    points.forEach((p) => {
        if (out.some((q) => Math.hypot(q.x - p.x, q.y - p.y) <= eps)) return;
        out.push({ x: p.x, y: p.y });
    });
    return out;
};

/** Center, half-axes (image px), and width-edge angle for a flat rectangle (incl. rotated). */
export const getFlatRectOrientedGeometry = (segments, rectId) => {
    const segs = segments.filter((s) => s.closedRectId === rectId);
    if (!segs.length) return null;

    let bestSeg = null;
    let bestLen = 0;
    segs.forEach((s) => {
        if (s.x1 == null || s.x2 == null) return;
        const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1);
        if (len > bestLen) {
            bestLen = len;
            bestSeg = s;
        }
    });
    if (!bestSeg || bestLen < 1e-3) return null;

    const angleRad = Math.atan2(bestSeg.y2 - bestSeg.y1, bestSeg.x2 - bestSeg.x1);
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const points = [];
    segs.forEach((s) => {
        if (s.x1 != null && s.y1 != null) points.push({ x: s.x1, y: s.y1 });
        if (s.x2 != null && s.y2 != null) points.push({ x: s.x2, y: s.y2 });
    });
    if (!points.length) return null;

    const corners = mergeNearPoints(points, Math.max(0.75, bestLen * 0.02));
    const cx = corners.reduce((s, p) => s + p.x, 0) / corners.length;
    const cy = corners.reduce((s, p) => s + p.y, 0) / corners.length;

    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    points.forEach(({ x, y }) => {
        const dx = x - cx;
        const dy = y - cy;
        const u = dx * cos + dy * sin;
        const v = -dx * sin + dy * cos;
        minU = Math.min(minU, u);
        maxU = Math.max(maxU, u);
        minV = Math.min(minV, v);
        maxV = Math.max(maxV, v);
    });

    const halfW = (maxU - minU) / 2;
    const halfL = (maxV - minV) / 2;
    if (halfW < 1e-3 || halfL < 1e-3) return null;

    return { cx, cy, angleRad, halfW, halfL };
};

/** Longest-edge angle (rad) of a bin-local footprint polygon in layout XZ. */
export const flatSideAngleRadFromPolygon = (poly) => {
    if (!poly || poly.length < 2) return null;
    let bestLen = 0;
    let bestAng = 0;
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const len = Math.hypot(dx, dz);
        if (len > bestLen) {
            bestLen = len;
            bestAng = Math.atan2(dz, dx);
        }
    }
    return bestLen > 0 ? bestAng : null;
};

/** Legacy Site 3D cable spin (+180° at deg=0). Prefer btsCableRadiansFromDeg for BTS topPointX/Z. */
export const productionCableRadiansFromDeg = (deg) => {
    if (deg == null || !Number.isFinite(deg)) return null;
    return deg < 180
        ? (((deg % 180) + 180) * Math.PI) / 180
        : ((deg + 180) * Math.PI) / 180;
};

/** Match bin3dModel wall spin — BTS topPointX/Z are bin-local catalog feet. */
export const btsCableRadiansFromDeg = (deg) => {
    if (deg == null || !Number.isFinite(deg)) return null;
    return -(deg * Math.PI) / 180;
};

/**
 * Major-axis angle (rad) of a footprint polygon via 2nd-moment (PCA).
 * Robust to heavy tessellation (curved walls → hundreds of tiny edges),
 * unlike longest-single-edge. Direction-agnostic (axis, not vector).
 */
export const principalAxisAngleRad = (poly) => {
    if (!poly || poly.length < 3) return null;
    let cx = 0;
    let cz = 0;
    poly.forEach((p) => {
        cx += p.x;
        cz += p.z;
    });
    cx /= poly.length;
    cz /= poly.length;
    let sxx = 0;
    let szz = 0;
    let sxz = 0;
    poly.forEach((p) => {
        const dx = p.x - cx;
        const dz = p.z - cz;
        sxx += dx * dx;
        szz += dz * dz;
        sxz += dx * dz;
    });
    if (sxx + szz < 1e-9) return null;
    return 0.5 * Math.atan2(2 * sxz, sxx - szz);
};

/** Catalog (un-rotated) footprint major axis: length runs along Z, width along X. */
export const catalogMajorAxisAngleRad = (layoutBin, site = null) => {
    const bin =
        layoutBin?.bin ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        );
    const w = bin?.width ?? layoutBin?.width ?? 0;
    const l = bin?.length ?? layoutBin?.length ?? 0;
    if (w <= 0 && l <= 0) return null;
    return l >= w ? Math.PI / 2 : 0;
};

export const flatSideAngleRadFromLayoutBin = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return null;
    const fromPoly = flatSideAngleRadFromPolygon(layoutBin.previewPolygonFt);
    if (fromPoly != null) return fromPoly;
    const sides =
        layoutBin.sides?.length > 0
            ? layoutBin.sides
            : (site?.layoutSides || []).filter(
                  (s) =>
                      s.customerSiteLayoutBinId ===
                      layoutBin.customerSiteLayoutBinId
              );
    return flatSideAngleRadFromPolygon(
        sides.flatMap((s) => [
            { x: s.x1, z: s.z1 },
            { x: s.x2, z: s.z2 },
        ])
    );
};

/** Wrap an angle (rad) into (-π/2, π/2] — principal axes are direction-agnostic. */
export const wrapAxisDeltaRad = (rad) => {
    let r = rad;
    while (r > Math.PI / 2) r -= Math.PI;
    while (r <= -Math.PI / 2) r += Math.PI;
    return r;
};

/**
 * True when saved flat sides / preview polygon already include orientation (editor rotation
 * baked into vertices). Axis-aligned footprints with separate degreesToNorth still return false
 * so bin3dModel gets the catalog spin and cables stay aligned.
 *
 * Cardinal footprints (long on X or Z) are never "baked" — a π/2 mismatch vs catalog after
 * Length/Width normalize is a convention difference, not a rotated polygon.
 */
export const flatFootprintRotationBakedInGeometry = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return false;
    const poly = layoutBinFootprintPoly(layoutBin, site);
    if (!poly || poly.length < 3) return false;
    const polyAxis = flatSideAngleRadFromPolygon(poly);
    if (polyAxis == null) return false;
    const cardinal =
        Math.abs(wrapAxisDeltaRad(polyAxis)) < 0.02 ||
        Math.abs(wrapAxisDeltaRad(polyAxis - Math.PI / 2)) < 0.02;
    if (cardinal) return false;
    const catalogAxis = catalogMajorAxisAngleRad(layoutBin, site);
    if (catalogAxis == null) return false;
    return Math.abs(wrapAxisDeltaRad(polyAxis - catalogAxis)) > 0.02;
};

/**
 * Wall footprint polygon in bin-local feet — must match SticeArcWall.
 * Editor layouts: previewPolygonFt is the 2D face outline (source of truth).
 * Legacy: prefer site.layoutSides so cables and walls share one footprint.
 */
export const layoutBinFootprintPoly = (layoutBin, site) => {
    if (!layoutBin) return null;

    if (
        siteUsesEditorLayoutRotationFromSite(site) &&
        layoutBin.previewPolygonFt?.length >= 3
    ) {
        return layoutBin.previewPolygonFt;
    }

    const siteSides = (site?.layoutSides || []).filter(
        (s) =>
            s.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId
    );
    const sides =
        siteSides.length >= 3
            ? siteSides
            : layoutBin?.sides?.length >= 3
              ? layoutBin.sides
              : null;

    if (sides?.length) {
        // Prefer drawSite locals; otherwise subtract layoutBin origin.
        // Chain into a closed loop — flatMapping both endpoints of every side
        // produces a zigzag (duplicate corners) that skews walls/cables.
        const hasDrawLocal = sides.every(
            (s) =>
                Number.isFinite(Number(s.lx1)) &&
                Number.isFinite(Number(s.lz1)) &&
                Number.isFinite(Number(s.lx2)) &&
                Number.isFinite(Number(s.lz2))
        );
        const localSides = hasDrawLocal
            ? sides
            : (() => {
                  const bx = Number(layoutBin?.x) || 0;
                  const bz = Number(layoutBin?.z) || 0;
                  return sides.map((s) => ({
                      ...s,
                      lx1: (Number(s.x1) || 0) - bx,
                      lz1: (Number(s.z1) || 0) - bz,
                      lx2: (Number(s.x2) || 0) - bx,
                      lz2: (Number(s.z2) || 0) - bz,
                  }));
              })();
        const ordered = chainLayoutSidesLocal(
            localSides,
            chainTolForLayoutSides(localSides)
        );
        if (ordered.length >= 3) {
            const poly = [];
            ordered.forEach((s) => {
                if (Number.isFinite(Number(s.lx1)) && Number.isFinite(Number(s.lz1))) {
                    poly.push({ x: Number(s.lx1), z: Number(s.lz1) });
                }
            });
            const last = ordered[ordered.length - 1];
            if (
                Number.isFinite(Number(last?.lx2)) &&
                Number.isFinite(Number(last?.lz2))
            ) {
                const d = poly.length
                    ? Math.hypot(poly[0].x - last.lx2, poly[0].z - last.lz2)
                    : 1;
                if (d > 0.05) poly.push({ x: Number(last.lx2), z: Number(last.lz2) });
            }
            if (poly.length >= 3) return poly;
        }
    }

    if (layoutBin?.previewPolygonFt?.length >= 3) {
        return layoutBin.previewPolygonFt;
    }
    return null;
};

/**
 * Oriented flat footprint in bin-local feet (true building size + yaw).
 * AABB of a rotated rectangle is inflated and axis-aligned — that was spreading
 * Crittenden cables outside the outlines. Cable grids use width=short, length=long
 * (BTS), then mapCatalogCablePointToOrientedFootprint rotates into the wall frame.
 */
export const orientedLayoutFootprintFt = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return null;
    const poly = layoutBinFootprintPoly(layoutBin, site);
    if (!poly || poly.length < 3) return null;

    let cx = 0;
    let cz = 0;
    poly.forEach((p) => {
        cx += p.x;
        cz += p.z;
    });
    cx /= poly.length;
    cz /= poly.length;

    let angleRad =
        principalAxisAngleRad(poly) ?? flatSideAngleRadFromPolygon(poly);
    if (angleRad == null) return null;

    const project = (ang) => {
        const cos = Math.cos(ang);
        const sin = Math.sin(ang);
        let minU = Infinity;
        let maxU = -Infinity;
        let minV = Infinity;
        let maxV = -Infinity;
        poly.forEach((p) => {
            const dx = p.x - cx;
            const dz = p.z - cz;
            const u = dx * cos + dz * sin;
            const v = -dx * sin + dz * cos;
            minU = Math.min(minU, u);
            maxU = Math.max(maxU, u);
            minV = Math.min(minV, v);
            maxV = Math.max(maxV, v);
        });
        return {
            extentU: maxU - minU,
            extentV: maxV - minV,
            midU: (minU + maxU) / 2,
            midV: (minV + maxV) / 2,
        };
    };

    let proj = project(angleRad);
    // Principal axis is unsigned; align angle with the longer side (Length / bunks).
    if (proj.extentV > proj.extentU * 1.02) {
        angleRad += Math.PI / 2;
        proj = project(angleRad);
    }

    const length = proj.extentU;
    const width = proj.extentV;
    if (!(width > 0.5 && length > 0.5)) return null;

    // Shift center to the oriented rectangle midpoint (not vertex average).
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const midCx = cx + proj.midU * cos - proj.midV * sin;
    const midCz = cz + proj.midU * sin + proj.midV * cos;

    return {
        width,
        length,
        angleRad,
        cx: midCx,
        cz: midCz,
    };
};

/** Linked BTS bin row for a layout bin. */
const linkedBinForLayoutBin = (layoutBin, site = null) =>
    layoutBin?.bin ??
    (site?.bins || []).find(
        (b) =>
            sameBinDataIdLoose(b.binDataId, layoutBin?.binId) ||
            sameBinDataIdLoose(b.binId, layoutBin?.binId)
    );

/** Footprint span in feet from preview polygon or layout-bin width/length. */
export const layoutFootprintSpanFt = (layoutBin, site = null) => {
    const poly = layoutBinFootprintPoly(layoutBin, site);
    if (poly?.length >= 3) {
        let minX = Infinity;
        let maxX = -Infinity;
        let minZ = Infinity;
        let maxZ = -Infinity;
        poly.forEach((p) => {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minZ = Math.min(minZ, p.z);
            maxZ = Math.max(maxZ, p.z);
        });
        return { spanX: maxX - minX, spanZ: maxZ - minZ };
    }

    const layoutW = Number(layoutBin?.width) || 0;
    const layoutL = Number(layoutBin?.length) || 0;
    const bin = linkedBinForLayoutBin(layoutBin, site);
    const binW = Number(bin?.width) || 0;
    const binL = Number(bin?.length) || 0;

    // initializeSiteLayout stores half-extents; linked BTS bin has full field feet.
    if (layoutW > 0 && binW > layoutW * 1.35 && binL > 0) {
        return { spanX: binW, spanZ: binL };
    }

    const sides = (site?.layoutSides || []).filter(
        (s) =>
            s.customerSiteLayoutBinId === layoutBin?.customerSiteLayoutBinId
    );
    if (sides.length >= 3 && layoutW > 0 && layoutL > 0) {
        return { spanX: layoutW * 2, spanZ: layoutL * 2 };
    }

    return { spanX: layoutW, spanZ: layoutL };
};

/** Standard BTS flat-house cable grid (width E–W, length N–S), regardless of bogus bin W/L. */
export const CANONICAL_FLAT_HOUSE_CATALOG_FT = { width: 90, length: 300 };

const layoutBinFlatCableCount = (layoutBin, site = null) => {
    if (!layoutBin) return 0;
    const binId = layoutBin.customerSiteLayoutBinId;
    const bn = String(layoutBin.binName || "")
        .toLowerCase()
        .replace(/\s+/g, "");
    return (site?.layoutCables || []).filter((lc) => {
        if (binId != null && lc.customerSiteLayoutBinId === binId) return true;
        const prefix = String(lc?.cableName || lc?.name || "")
            .replace(/[-\s]*\d+\s*$/, "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "");
        if (!prefix || !bn) return false;
        // "b" → "bighouse", "s" → "smallhouse"; do not count sibling-bin cables.
        if (bn.startsWith(prefix) || prefix.startsWith(bn) || bn === prefix) {
            const otherOwns = (site?.layoutBins || []).some((lb) => {
                if (lb.customerSiteLayoutBinId === binId) return false;
                const obn = String(lb.binName || "")
                    .toLowerCase()
                    .replace(/\s+/g, "");
                return obn && (obn.startsWith(prefix) || prefix.startsWith(obn));
            });
            return !otherOwns;
        }
        return false;
    }).length;
};

/** BTS Width/Length on flat houses are often placeholders (e.g. 35×35); use layout footprint instead. */
const btsFlatDimensionsArePlaceholder = (bin, layoutBin = null) => {
    if (!binLooksLikeFlatHouse(bin, layoutBin)) return false;
    const w = Number(bin?.width) || 0;
    const l = Number(bin?.length) || 0;
    if (!(w > 0 && l > 0)) return true;
    if (Math.abs(w - l) <= Math.max(w, l) * 0.2) return true;
    return l < w * 1.4;
};

/** Layout-bin footprint in feet for flat-house cable grids — same source as walls/compass. */
const flatHouseLayoutFootprintDimensions = (layoutBin, site = null) => {
    if (!layoutBin) return null;

    // Oriented short×long (BTS Width×Length) — not the axis-aligned AABB.
    const oriented = orientedLayoutFootprintFt(layoutBin, site);
    if (oriented) {
        return { width: oriented.width, length: oriented.length };
    }

    const { spanX, spanZ } = layoutFootprintSpanFt(layoutBin, site);
    if (spanX > 0 && spanZ > 0) {
        return { width: spanX, length: spanZ };
    }

    const lw = Number(layoutBin.width) || 0;
    const ll = Number(layoutBin.length) || 0;
    if (lw > 0 && ll > 0) {
        return { width: lw, length: ll };
    }
    return null;
};

const binLooksLikeFlatHouse = (bin, layoutBin = null) => {
    const label = String(
        bin?.type || bin?.binName || layoutBin?.binName || ""
    ).toLowerCase();
    return /flat|house/.test(label);
};

const layoutBinLooksLikeLongFlat = (layoutBin, site = null) => {
    if (!layoutBin) return false;
    const { spanX, spanZ } = layoutFootprintSpanFt(layoutBin, site);
    const w = spanX > 0 ? spanX : Number(layoutBin.width) || 0;
    const l = spanZ > 0 ? spanZ : Number(layoutBin.length) || 0;
    if (!(w > 0 && l > 0)) return false;
    return Math.max(w, l) / Math.min(w, l) > 1.3;
};

/** Saved layout walls run long on X (e.g. Lincoln 150×40 stored with width as long side). */
export const flatBinLayoutFootprintLongOnX = (layoutBin, site = null) => {
    const { spanX, spanZ } = layoutFootprintSpanFt(layoutBin, site);
    if (spanX > 0 && spanZ > 0) return spanX > spanZ * 1.05;
    const w = Number(layoutBin?.width ?? 0);
    const l = Number(layoutBin?.length ?? 0);
    return w > l * 1.05;
};

/**
 * Layout footprint long on X with a 3-10 flat house: scale canonical 90×300 cable grid
 * into layout-bin feet — do not rotate the whole group.
 */
export const flatBinUsesCatalogCableScale = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return false;
    const bin = linkedBinForLayoutBin(layoutBin, site);
    if (!binLooksLikeFlatHouse(bin, layoutBin)) return false;
    if (!parseFlatCableLayoutGrid(bin?.cableLayout)) return false;
    return flatBinLayoutFootprintLongOnX(layoutBin, site);
};

/** Compass / wall half-extents: layout feet when scaling cables, else catalog-normalized. */
export const flatCompassHalfExtentsFt = (layoutBin, site = null) => {
    if (flatBinUsesCatalogCableScale(layoutBin, site)) {
        const { spanX, spanZ } = layoutFootprintSpanFt(layoutBin, site);
        const w = spanX > 0 ? spanX : Number(layoutBin?.width) || 30;
        const l = spanZ > 0 ? spanZ : Number(layoutBin?.length) || 30;
        return { hw: w / 2, hl: l / 2 };
    }
    const { width, length } = catalogFootprintDimensionsFromLayoutBin(
        layoutBin,
        site
    );
    return { hw: (width || 30) / 2, hl: (length || 30) / 2 };
};

/**
 * Extra Y rotation (rad) so flat walls + BTS cable grid match field orientation
 * (south on the long wall). Applied equally to bin3dModel, cables, and compass.
 */
export const flatBinProductionRotationCorrectionRad = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return 0;
    if (flatFootprintRotationBakedInGeometry(layoutBin, site)) return 0;
    if (flatBinUsesCatalogCableScale(layoutBin, site)) return 0;

    const bin = linkedBinForLayoutBin(layoutBin, site);
    if (!isFlatThreeTenRowMajorLayout(bin, layoutBin, site)) return 0;

    // Layout long on Z: quarter-turn so south sits on the long wall. Long-on-X layouts
    // already match the cable grid via flatBinUsesCatalogCableScale (no extra spin).
    return -Math.PI / 2;
};

export const siteUsesEditorLayoutRotationFromSite = (site) =>
    !!(
        site?.isLayoutPreview ||
        site?.isLayoutPreviewSite ||
        site?.isLayoutPreview3d ||
        site?.layoutUsesEditorLayoutAxes ||
        site?.layoutSavedFromSiteLayoutEditor
    );

/** Y rotation for the whole flat stice group (walls + cables + compass). */
export const flatProductionGroupRotationY = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return 0;
    if (siteUsesEditorLayoutRotationFromSite(site)) return 0;
    if (flatFootprintRotationBakedInGeometry(layoutBin, site)) return 0;
    // Polygon footprint: walls already carry orientation; cables map via
    // mapFlatCablePointToWallFootprint — DegreesToNorth is BTS labels only.
    if (orientedLayoutFootprintFt(layoutBin, site)) return 0;
    // Long-on-X layout footprints: walls/cables stay in layout axes. DegreesToNorth from
    // Length/Width normalize is for BTS labels only — spinning here misaligns the grid.
    if (flatBinUsesCatalogCableScale(layoutBin, site)) return 0;

    const bin =
        layoutBin?.bin ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        );
    const deg = bin?.degreesToNorth ?? layoutBin?.rotation ?? 0;
    return (
        -(deg * Math.PI) / 180 +
        flatBinProductionRotationCorrectionRad(layoutBin, site)
    );
};

/** Per-cable spin — omit quarter-turn when the parent group already rotates. */
export const flatProductionCableRotationRad = (layoutBin, site = null) => {
    if (!layoutBin || layoutBin.isRound) return null;
    if (siteUsesEditorLayoutRotationFromSite(site)) return null;
    if (flatFootprintRotationBakedInGeometry(layoutBin, site)) return null;
    if (orientedLayoutFootprintFt(layoutBin, site)) return null;
    // Long-on-X layout: parent group stays unspun; DegreesToNorth is BTS-only.
    if (flatBinUsesCatalogCableScale(layoutBin, site)) return null;

    const bin =
        layoutBin?.bin ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        );
    const deg = bin?.degreesToNorth ?? layoutBin?.rotation ?? 0;
    const base = btsCableRadiansFromDeg(deg);

    if (Math.abs(flatProductionGroupRotationY(layoutBin, site)) > 0.001) {
        return base;
    }

    const correction = flatBinProductionRotationCorrectionRad(layoutBin, site);
    if (base == null) return correction || null;
    return base + correction;
};

/** Unspun N/S/E/W label positions (parent group applies wall rotation). */
export const catalogNorthCompassBasePositions = (layoutBin, site = null) => {
    const { hw, hl } = flatCompassHalfExtentsFt(layoutBin, site);
    const m = 10;
    return {
        north: [0, -2.25, -hl - m],
        south: [0, -2.25, hl + m],
        east: [hw + m, -2.25, 0],
        west: [-hw - m, -2.25, 0],
    };
};

/** Parse flat `CableLayout` like `3-10` → raw { cols, rows } from string order. */
export const parseFlatCableLayoutGrid = (cableLayout) => {
    if (cableLayout == null || cableLayout === "") return null;
    const parts = String(cableLayout)
        .split("-")
        .map((n) => parseInt(String(n).trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
    if (parts.length === 1) {
        // BTS often stores only cables-per-bunk ("3"); bunks come from cable count.
        return { cols: parts[0], rows: null };
    }
    if (parts.length !== 2) return null;
    return { cols: parts[0], rows: parts[1] };
};

/**
 * BTS FlatHouse3D CableLayout: Layout[0] = cables per bunk (along Width).
 * Bunks run along Length = ceil(cableCount / cablesPerBunk) (Layout[1] is optional hint).
 *
 * Map into SW row-major grid (cols along X/width, rows along Z/length):
 * - Length-long / catalog frame (length ≥ width): cols = per bunk, rows = bunks
 * - Layout long-on-X (width > length): axes swapped → cols = bunks, rows = per bunk
 */
export const flatHouseGridFromCableLayout = (
    cableLayout,
    width,
    length,
    cableCount = null
) => {
    const parsed = parseFlatCableLayoutGrid(cableLayout);
    const w = Number(width) || 0;
    const l = Number(length) || 0;
    const count = Math.max(0, Math.round(Number(cableCount)) || 0);

    let cablesPerBunk = parsed?.cols ?? null;
    let bunks = parsed?.rows ?? null;

    // Missing CableLayout (Crittenden Small House): BTS defaults cablesPerBunk to 2.
    if (!(cablesPerBunk > 0) && count > 0) {
        cablesPerBunk = 2;
    }
    if (!(cablesPerBunk > 0)) {
        if (w > 0 && l > w * 2) return { cols: 10, rows: 3 };
        return null;
    }

    if (!(bunks > 0) && count > 0) {
        bunks = Math.ceil(count / cablesPerBunk);
    }
    // Stale "3-10" (or similar) when the bin has more cables than the hint product.
    if (
        count > 0 &&
        cablesPerBunk > 0 &&
        bunks > 0 &&
        cablesPerBunk * bunks < count
    ) {
        bunks = Math.ceil(count / cablesPerBunk);
    }
    if (!(bunks > 0)) {
        if (w > 0 && l > w * 2) return { cols: 10, rows: 3 };
        return null;
    }

    // Long-on-X layout footprint: Length axis lies on X → bunks along cols.
    if (w > 0 && l > 0 && w > l * 1.05) {
        return { cols: bunks, rows: cablesPerBunk };
    }
    // Catalog / BTS Length-long: bunks along Z (rows), cables-per-bunk along X (cols).
    return { cols: cablesPerBunk, rows: bunks };
};

/**
 * Uniform flat-house cable grid: equal gaps including margins to the walls,
 * same rule as BTS bunk placement (`dim / (count + 1)`).
 * Catalog feet: N = −Z, S = +Z, W = −X, E = +X.
 */
export const uniformFlatGridPointXZ = (col, row, width, length, numCols, numRows) => {
    const w = Number(width) || 0;
    const l = Number(length) || 0;
    const cols = Math.max(1, Math.round(Number(numCols)) || 1);
    const rows = Math.max(1, Math.round(Number(numRows)) || 1);
    if (!(w > 0 && l > 0)) return null;
    if (col < 0 || col >= cols || row < 0 || row >= rows) return null;

    const xSpacing = w / (cols + 1);
    const zSpacing = l / (rows + 1);
    return {
        topPointX: -w / 2 + (col + 1) * xSpacing,
        topPointZ: l / 2 - (row + 1) * zSpacing,
    };
};

/**
 * 10×3 (or cols×rows) grid: cable 1 at southwest, each row runs west→east, rows stack south→north.
 */
export const topPointFromSouthwestRowMajorGrid = (
    cableNumber,
    width,
    length,
    numCols,
    numRows
) => {
    const n = Math.round(Number(cableNumber));
    if (!Number.isFinite(n) || n < 1 || n > numCols * numRows) return null;
    if (!width || !length) return null;

    const col = (n - 1) % numCols;
    const row = Math.floor((n - 1) / numCols);
    return uniformFlatGridPointXZ(col, row, width, length, numCols, numRows);
};

/**
 * BTS FlatHouse3D cable order: cablesPerBunk along Width, then next bunk along Length.
 * `width`/`length` are the grid footprint feet (layout long-on-X or catalog Length-long).
 */
export const topPointFromFlatHouseBunkMajorGrid = (
    cableNumber,
    width,
    length,
    cablesPerBunk,
    bunks
) => {
    const n = Math.round(Number(cableNumber));
    const per = Math.max(1, Math.round(Number(cablesPerBunk)) || 1);
    const bunkCount = Math.max(1, Math.round(Number(bunks)) || 1);
    if (!Number.isFinite(n) || n < 1 || n > per * bunkCount) return null;
    if (!width || !length) return null;

    const onBunk = (n - 1) % per;
    const bunk = Math.floor((n - 1) / per);
    const w = Number(width) || 0;
    const l = Number(length) || 0;

    // Long-on-X footprint: Length (bunk axis) → X, Width (per-bunk) → Z.
    if (w > 0 && l > 0 && w > l * 1.05) {
        return uniformFlatGridPointXZ(
            bunk,
            onBunk,
            w,
            l,
            bunkCount,
            per
        );
    }
    // Catalog / BTS Length-long: Width → X, Length → Z.
    return uniformFlatGridPointXZ(onBunk, bunk, w, l, per, bunkCount);
};

/** Place flat-house cable n using CableLayout bunk semantics + footprint orientation. */
export const topPointFromFlatHouseCableLayout = (
    cableNumber,
    cableLayout,
    width,
    length,
    cableCount = null
) => {
    const grid = flatHouseGridFromCableLayout(
        cableLayout,
        width,
        length,
        cableCount
    );
    if (!grid) return null;
    const w = Number(width) || 0;
    const l = Number(length) || 0;
    // flatHouseGridFromCableLayout stores visual cols/rows; recover bunk semantics.
    const cablesPerBunk = w > l * 1.05 ? grid.rows : grid.cols;
    const bunks = w > l * 1.05 ? grid.cols : grid.rows;
    return topPointFromFlatHouseBunkMajorGrid(
        cableNumber,
        width,
        length,
        cablesPerBunk,
        bunks
    );
};

/**
 * BTS Site Setup / Cable table XZ — same uniform (count+1) spacing as FlatHouse3D bunks.
 * Always solved (and returned) in the long-on-X frame, whichever way the row stores its sides —
 * these feed the Cable TopPointX/Z columns, which Site 3D reads back against the layout
 * footprint. `btsFlatHouseOverrideFromTopPoint` handles the turn into BTS bin-local feet.
 */
export const btsFlatHouseCableTopPointXZ = (cableNumber, bin) => {
    if (!bin) return null;
    const binWidth = Number(bin.width) || 0;
    const binLength = Number(bin.length) || 0;
    if (!(binWidth > 0 && binLength > 0)) return null;

    const width = Math.max(binWidth, binLength);
    const length = Math.min(binWidth, binLength);

    return topPointFromFlatHouseCableLayout(
        cableNumber,
        bin.cableLayout,
        width,
        length,
        bin?.cableList?.length
    );
};

/**
 * BTS FlatHouse3D default bunk placement (IsPositionOverride=false).
 * Cable 1 lands northeast; use relative overrides to rotate grid to southwest.
 */
export const btsFlatHouseBunkPointXZ = (position, bin, totalCables) => {
    const parts = String(bin?.cableLayout ?? "3-10").split("-");
    let cablesPerBunk = parseInt(parts[0], 10);
    if (!Number.isFinite(cablesPerBunk) || cablesPerBunk <= 0) cablesPerBunk = 2;

    const width = Number(bin?.width) || 0;
    const length = Number(bin?.length) || 0;
    const count = Math.max(1, Math.round(Number(totalCables)) || 0);
    const pos = Math.round(Number(position));
    if (!(width > 0 && length > 0) || !Number.isFinite(pos) || pos < 1) {
        return null;
    }

    const hWidth = width / 2;
    const hLength = length / 2;
    const bunks = Math.ceil(count / cablesPerBunk);
    const bunkSpacing = length / (bunks + 1);

    let lastBunk = 0;
    let cableMultiplier = 1;
    let bayDistance = 0;
    let cableDistance = 0;
    let cableSpacing = 0;
    let cablesLeft = count;

    for (let i = 0; i < pos; i += 1) {
        const bunk = Math.ceil((i + 1) / cablesPerBunk);
        if (bunk !== lastBunk) {
            cableMultiplier = 1;
            bayDistance = bunk * bunkSpacing;
            const cablesOnThisBunk =
                cablesLeft >= cablesPerBunk ? cablesPerBunk : cablesLeft;
            cableSpacing = width / (cablesOnThisBunk + 1);
        }
        lastBunk = bunk;
        cableDistance = cableSpacing * cableMultiplier;
        cableMultiplier += 1;
        cablesLeft -= 1;
    }

    return {
        topPointX: hWidth - cableDistance,
        topPointZ: -hLength + bayDistance,
    };
};

/** BTS House3D.StartCablePoint — east/wall-top/north-west corner in bin-local feet. */
export const btsFlatHouseStartCablePoint = (bin) => {
    const width = Number(bin?.width) || 0;
    const length = Number(bin?.length) || 0;
    const wallHeight = Number(bin?.height) || Number(bin?.diameter) || 35;
    if (!(width > 0 && length > 0)) return null;
    const hWidth = width / 2;
    const hLength = length / 2;
    const hWallHeight = wallHeight / 2;
    return { x: hWidth, y: hWallHeight, z: -hLength };
};

/**
 * FlatHouse3D IsOffsetFromOrigin overrides (Wpf3D.cs):
 *   X = StartCablePoint.X - OverrideOffsetX
 *   Z = StartCablePoint.Z + OverrideOffsetZ
 */
export const btsFlatHouseOverrideFromTopPoint = (bin, topPointX, topPointZ) => {
    const start = btsFlatHouseStartCablePoint(bin);
    if (!start) return null;
    let x = Number(topPointX);
    let z = Number(topPointZ);
    if (!Number.isFinite(x) || !Number.isFinite(z)) return null;

    // Grid XZ arrives long-on-X in grainwavedev catalog feet (N=−Z, S=+Z, W=−X, E=+X).
    // After Length↔Width normalize, BTS houses are long on Z and DegreesToNorth is +90°, which
    // puts BTS N on +X / S on −X / W on −Z / E on +Z. Map SW→SW with (x, z) → (−z, x).
    if ((Number(bin?.length) || 0) > (Number(bin?.width) || 0)) {
        const rotatedX = -z;
        z = x;
        x = rotatedX;
    }

    return {
        overrideOffsetX: start.x - x,
        overrideOffsetZ: z - start.z,
    };
};

/**
 * BTS `anglePosition` / position slot → catalog topPointX/Z (feet, N = −Z, E = +X).
 * Rows run north→south; within each row slots run east→west (position 1 = NE).
 */
export const topPointFromBtsAnglePosition = (
    anglePosition,
    width,
    length,
    cableLayout
) => {
    const grid = parseFlatCableLayoutGrid(cableLayout);
    if (!grid || grid.rows == null || !width || !length) return null;

    const { cols, rows } = grid;
    const pos = Math.round(Number(anglePosition));
    if (!Number.isFinite(pos) || pos < 1 || pos > cols * rows) return null;

    const row = Math.floor((pos - 1) / cols);
    const colFromEast = (pos - 1) % cols;
    // Convert E→W slot index into the W→E column used by the uniform SW grid.
    const col = cols - 1 - colFromEast;
    return uniformFlatGridPointXZ(col, row, width, length, cols, rows);
};

/** Full catalog width/length (ft) — layout bins sometimes store half-extents. */
export const catalogFlatBinDimensions = (bin) => {
    let width = Number(bin?.width) || 0;
    let length = Number(bin?.length) || 0;
    // BTS sometimes stores the long side as width (e.g. 300×90); catalog length runs N–S.
    if (width > 0 && length > 0 && width > length) {
        const t = width;
        width = length;
        length = t;
    }
    // Layout tables sometimes store half-width (~45 ft instead of 90).
    if (
        width > 0 &&
        length > 0 &&
        width >= 20 &&
        width <= 50 &&
        length >= width * 4
    ) {
        width *= 2;
    }
    // Half-length (~150 ft instead of 300).
    if (
        width > 0 &&
        length > 0 &&
        width >= 70 &&
        length >= 130 &&
        length <= 170
    ) {
        length *= 2;
    }
    return { width, length };
};

/** Catalog width (E–W) / length (N–S) for a layout bin — same normalization as cable grid. */
export const catalogFootprintDimensionsFromLayoutBin = (layoutBin, site = null) => {
    const bin =
        layoutBin?.bin ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        );
    if (bin) return catalogFlatBinDimensions(bin);
    return catalogFlatBinDimensions({
        width: layoutBin?.width,
        length: layoutBin?.length,
    });
};

/** BTS grid slot from cable row (`position` or `anglePosition`). */
export const btsCableGridSlot = (cable) => {
    if (!cable) return null;
    const slot = cable.position ?? cable.anglePosition;
    const n = Math.round(Number(slot));
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Per-bin cable index for flat grids (B-1 → 1, S-12 → 12).
 * Prefer the name suffix: layout `binOrder` is often a site-wide sequence
 * (Crittenden Small House S-1 has binOrder 79 after Big House's 78 cables).
 */
export const cableNumberFromCableRecord = (cable) => {
    const m = String(cable?.name || cable?.cableName || "").match(/(\d+)\s*$/);
    if (m) {
        const fromName = parseInt(m[1], 10);
        if (fromName >= 1) return fromName;
    }
    if (cable?.number != null && Number.isFinite(Number(cable.number))) {
        const n = Math.round(Number(cable.number));
        if (n >= 1) return n;
    }
    return null;
};

/** Non-round bin with BTS `3-10` → 10×3 SW row-major (cable count may drift after setup edits). */
export const isFlatThreeTenRowMajorLayout = (
    bin,
    layoutBin = null,
    site = null
) => {
    const parsed = parseFlatCableLayoutGrid(bin?.cableLayout);
    if (parsed?.cols === 3 && parsed.rows === 10) {
        return (
            binLooksLikeFlatHouse(bin, layoutBin) ||
            layoutBinLooksLikeLongFlat(layoutBin, site)
        );
    }

    const cableCount =
        layoutBinFlatCableCount(layoutBin, site) ||
        bin?.cableList?.length ||
        0;
    if (cableCount !== 30) return false;

    const { width, length } = catalogFlatBinDimensions(bin || {});
    return width > 0 && length > width * 2;
};

/** Grid dimensions for flat-house cable placement — wall footprint wins over BTS W/L. */
export const flatHouseCableGridDimensions = (
    bin,
    layoutBin = null,
    site = null
) => {
    const layoutFootprint =
        layoutBin && !layoutBin.isRound
            ? flatHouseLayoutFootprintDimensions(layoutBin, site)
            : null;

    // Crittenden Small House etc.: walls come from the layout polygon. Never fall through
    // to a larger linked-bin Width/Length when we already know the footprint.
    if (layoutFootprint) {
        return layoutFootprint;
    }

    if (isFlatThreeTenRowMajorLayout(bin, layoutBin, site)) {
        return { ...CANONICAL_FLAT_HOUSE_CATALOG_FT };
    }

    if (bin) return catalogFlatBinDimensions(bin);
    return catalogFlatBinDimensions({
        width: layoutBin?.width,
        length: layoutBin?.length,
    });
};

const sameBinDataIdLoose = (a, b) => {
    if (a == null || b == null) return false;
    const na = parseInt(String(a), 10);
    const nb = parseInt(String(b), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
    return String(a) === String(b);
};

/** @deprecated use isFlatThreeTenRowMajorLayout */
export const shouldUseSouthwestRowMajorGrid = (bin) =>
    isFlatThreeTenRowMajorLayout(bin);

const layoutGroupForBin = (site, layoutBin) => {
    if (!layoutBin || !site?.layoutGroups?.length) return null;
    return (
        site.layoutGroups.find(
            (g) =>
                g.customerSiteLayoutGroupId ===
                layoutBin.customerSiteLayoutGroupId
        ) ??
        site.layoutGroups[0] ??
        null
    );
};

/** Half-width (ft) for round layout bins — editor footprint or BTS catalog diameter. */
export const roundBinRadiusFt = (layoutBin, site = null) => {
    if (!layoutBin) return 0;
    const w = Number(layoutBin.width) || 0;
    const l = Number(layoutBin.length) || 0;
    const span = w > 0 ? w : l;
    if (span > 0) return span / 2;
    const bin = linkedBinForLayoutBin(layoutBin, site);
    const d =
        Number(bin?.diameter) || Number(bin?.width) || Number(bin?.length) || 0;
    return d > 0 ? d / 2 : FALLBACK_LAYOUT_BIN_DIAMETER_FT / 2;
};

/** Max |XZ| for a cable top point to be treated as bin-local feet. */
export const cableTopPointMaxRadiusFt = (layoutBin, bin = null, site = null) => {
    if (layoutBin?.isRound) {
        return roundBinRadiusFt(layoutBin, site) * 1.2;
    }
    const { spanX, spanZ } = layoutFootprintSpanFt(layoutBin, site);
    const fromLayout = Math.max(spanX, spanZ, 0) / 2;
    if (fromLayout > 0) return fromLayout * 1.1;
    const linked = bin ?? linkedBinForLayoutBin(layoutBin, site);
    const { width, length } = catalogFlatBinDimensions(linked || {});
    return (Math.max(width, length, 30) / 2) * 1.1;
};

/**
 * Temputech rows / BTS sometimes store group- or layout-absolute XZ on cable rows.
 * Pick the candidate that lands inside the bin footprint (bin-local feet).
 */
export const normalizeCableTopPointToBinLocal = (
    x,
    z,
    layoutBin,
    site = null
) => {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
    if (!layoutBin) return { topPointX: x, topPointZ: z };

    const maxR = cableTopPointMaxRadiusFt(layoutBin, null, site);
    if (!(maxR > 0)) return { topPointX: x, topPointZ: z };

    const candidates = [];
    const tryCandidate = (lx, lz, priority) => {
        if (!Number.isFinite(lx) || !Number.isFinite(lz)) return;
        const r = Math.hypot(lx, lz);
        if (r <= maxR) {
            candidates.push({ topPointX: lx, topPointZ: lz, r, priority });
        }
    };

    tryCandidate(x, z, 0);

    const bx = layoutBin.x ?? 0;
    const bz = layoutBin.z ?? 0;
    const g = layoutGroupForBin(site, layoutBin);
    const gx = g?.x ?? 0;
    const gz = g?.z ?? 0;

    tryCandidate(x - bx, z - bz, 1);
    tryCandidate(x - gx - bx, z - gz - bz, 2);
    tryCandidate(x - gx, z - gz, 3);

    if (!candidates.length) return null;

    candidates.sort((a, b) => a.priority - b.priority);
    return {
        topPointX: candidates[0].topPointX,
        topPointZ: candidates[0].topPointZ,
    };
};

const normalizeBtsTopPoint = (pt, layoutBin, bin, site) => {
    if (!pt) return null;
    const norm = normalizeCableTopPointToBinLocal(
        pt.topPointX,
        pt.topPointZ,
        layoutBin,
        site
    );
    if (norm) return norm;
    const maxR = cableTopPointMaxRadiusFt(layoutBin, bin, site);
    if (
        maxR > 0 &&
        Math.hypot(pt.topPointX, pt.topPointZ) <= maxR * 1.05
    ) {
        return pt;
    }
    return null;
};

const roundTopPointFitsBin = (pt, layoutBin, site, slack = 1.02) => {
    if (!pt || !layoutBin?.isRound) return false;
    const maxR = roundBinRadiusFt(layoutBin, site) * slack;
    return maxR > 0 && Math.hypot(pt.topPointX, pt.topPointZ) <= maxR;
};

/** Parse round-bin BTS layer counts: "1-4-12" → [1, 4, 12], "0-8-12" → [0, 8, 12]. */
export const parseRoundBinCableLayers = (cableLayout) => {
    if (!cableLayout || typeof cableLayout !== "string") return null;
    const parts = cableLayout
        .split("-")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n >= 0);
    if (!parts.length) return null;
    return parts;
};

/** Layer string from BTS CableLayout, or null when not a round-bin layer pattern. */
const resolveRoundBinLayerCounts = (bin, layoutBin, site) => {
    const layers = parseRoundBinCableLayers(bin?.cableLayout);
    if (!layers?.length) return null;
    const sum = layers.reduce((a, b) => a + b, 0);
    if (sum <= 0) return null;

    const count = Math.max(
        bin?.cableList?.length ?? 0,
        layoutBinFlatCableCount(layoutBin, site) ?? 0
    );

    if (count > 0 && sum === count) return layers;
    // BTS layer strings (1-4-12, 0-8-12): 3+ segments, or leading 0/1 center layer.
    if (layers.length >= 3) return layers;
    if (layers.length === 2 && layers[0] <= 1) return layers;
    return null;
};

export const binIsRoundSilo = (bin, layoutBin = null) => {
    if (layoutBin?.isRound) return true;
    if (!bin) return false;
    const label = String(bin.type || bin.binName || layoutBin?.binName || "").toLowerCase();
    if (/flat|house/.test(label)) return false;
    if (Number(bin.diameter) > 0) return true;
    const w = Number(bin.width) || 0;
    const l = Number(bin.length) || 0;
    return w > 0 && l > 0 && Math.abs(w - l) < w * 0.05;
};

const mod360 = (deg) => ((deg % 360) + 360) % 360;

/** Match BinTemperature Bin3D PhaseShiftPassY (plan view X/Z, N = −Z). */
const phaseShiftPassYTopPointXZ = (angleDeg, radiusFt) => {
    if (!(radiusFt > 0)) return { topPointX: 0, topPointZ: 0 };
    const angle = mod360(angleDeg);
    const rad = (d) => (Math.PI * d) / 180;
    let x = 0;
    let z = 0;
    if (angle === 0) {
        x = radiusFt;
        z = 0;
    } else if (angle > 0 && angle < 90) {
        x = radiusFt * Math.cos(rad(angle));
        z = -radiusFt * Math.sin(rad(angle));
    } else if (angle === 90) {
        x = 0;
        z = -radiusFt;
    } else if (angle > 90 && angle < 180) {
        x = -radiusFt * Math.sin(rad(angle - 90));
        z = -radiusFt * Math.cos(rad(angle - 90));
    } else if (angle === 180) {
        x = -radiusFt;
        z = 0;
    } else if (angle > 180 && angle < 270) {
        x = -radiusFt * Math.cos(rad(angle - 180));
        z = radiusFt * Math.sin(rad(angle - 180));
    } else if (angle === 270) {
        x = 0;
        z = radiusFt;
    } else if (angle > 270 && angle < 360) {
        x = radiusFt * Math.sin(rad(angle - 270));
        z = radiusFt * Math.cos(rad(angle - 270));
    }
    return { topPointX: round1(x), topPointZ: round1(z) };
};

/**
 * BinTemperature round bin (Wpf3D.cs): layer walk, R = radius * ring / numLayers.
 * Replays cp 1..N with shared lca state (same as BTS foreach over cables).
 */
const roundBinLayerWalkForCable = (cableIndex1, layers, wallR) => {
    if (!layers?.length || !(wallR > 0) || cableIndex1 < 1) return null;
    const numRings = layers.length;
    let lca = 345.0;
    let lfca = 345.0;
    let cp = 0;

    for (let ring = 0; ring < numRings; ring++) {
        const io = layers[ring];
        if (!(io > 0)) continue;
        const Ab = 360.0 / io;
        const radiusFt = (wallR * ring) / numRings;

        for (let ringPosition = 0; ringPosition < io; ringPosition++) {
            cp++;

            if (io === 1 && ring === 0) {
                if (cp === cableIndex1) return { isCenter: true };
                continue;
            }

            let ac;
            if (ringPosition === 0 && ring === 1) {
                ac = lca;
            } else if (ringPosition === 0 && ring > 1) {
                ac = mod360(lfca - 2.5);
                lfca = ac;
            } else {
                ac = mod360(lca - Ab);
            }
            lca = ac;

            if (cp === cableIndex1) {
                return { isCenter: false, radiusFt, angleDeg: ac };
            }
        }
    }
    return null;
};

/** Bin3D layer walk uses 1-based cable list order (cp), not anglePosition or cable.number. */
export const roundBinCableWalkIndex = (cable, bin) => {
    const list = bin?.cableList || [];
    if (!list.length || !cable) return null;

    const cableId = cable.id ?? cable.cableId;
    if (cableId != null) {
        const byId = list.findIndex((c) => Number(c.id) === Number(cableId));
        if (byId >= 0) return byId + 1;
    }

    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
    const cableName = cable.name || cable.cableName;
    if (cableName) {
        const n = norm(cableName);
        const byName = list.findIndex((c) => norm(c.name) === n);
        if (byName >= 0) return byName + 1;
    }

    const fromRecord = cableNumberFromCableRecord(cable);
    if (fromRecord != null && fromRecord >= 1 && fromRecord <= list.length) {
        const sorted = [...list].sort(
            (a, b) => Number(a.number ?? 0) - Number(b.number ?? 0)
        );
        const idx = sorted.findIndex((c) => Number(c.number) === fromRecord);
        if (idx >= 0) return idx + 1;
    }

    return null;
};

/** Round silo cables from BTS CableLayout layers (1-4-12, 0-8-12, …). */
const topPointFromRoundBinBtsLayers = (cable, bin, layoutBin, site) => {
    const layers = resolveRoundBinLayerCounts(bin, layoutBin, site);
    const wallR = roundBinRadiusFt(layoutBin, site);
    if (!layers?.length || !(wallR > 0)) return null;

    const cableIndex = roundBinCableWalkIndex(cable, bin);
    if (cableIndex == null || cableIndex < 1) return null;

    const hit = roundBinLayerWalkForCable(cableIndex, layers, wallR);
    if (!hit) return null;
    if (hit.isCenter) return { topPointX: 0, topPointZ: 0 };
    return phaseShiftPassYTopPointXZ(hit.angleDeg, hit.radiusFt);
};

/** Site setup save leaves TopPointX/Z at 0; treat origin as unset so CableLayout grid runs. */
const isMeaningfulCableTopPoint = (x, z) =>
    Number.isFinite(x) &&
    Number.isFinite(z) &&
    (Math.abs(x) > 0.5 || Math.abs(z) > 0.5);

/** Use SW row-major grid when applicable; ignore stale 3-column DB top points. */
export const resolveBtsCableTopPoint = (cable, bin, layoutBin = null, site = null) => {
    if (!cable || !bin) return null;
    if (cable.aframeId != null && Number(cable.aframeId) > 0) return null;

    if (binIsRoundSilo(bin, layoutBin)) {
        const fromLayers = topPointFromRoundBinBtsLayers(
            cable,
            bin,
            layoutBin,
            site
        );
        if (fromLayers) return fromLayers;
        if (resolveRoundBinLayerCounts(bin, layoutBin, site)) {
            return null;
        }
    }

    const isThreeTen = isFlatThreeTenRowMajorLayout(bin, layoutBin, site);
    const { width, length } = flatHouseCableGridDimensions(bin, layoutBin, site);
    const cableNum = cableNumberFromCableRecord(cable);
    const cableCount =
        layoutBinFlatCableCount(layoutBin, site) ||
        bin?.cableList?.length ||
        0;

    // Place directly in the drawn wall frame (same sides as SticeArcWall).
    const oriented = layoutBin
        ? orientedLayoutFootprintFt(layoutBin, site)
        : null;
    if (
        layoutBin &&
        !layoutBin.isRound &&
        oriented &&
        cableNum != null &&
        (isThreeTen ||
            binLooksLikeFlatHouse(bin, layoutBin) ||
            !!flatHouseLayoutFootprintDimensions(layoutBin, site))
    ) {
        const fromOriented = topPointFromOrientedFlatFootprint(
            cableNum,
            oriented,
            bin?.cableLayout,
            cableCount
        );
        if (fromOriented) {
            return { ...fromOriented, wallLocal: true };
        }
    }

    const grid = flatHouseGridFromCableLayout(
        bin.cableLayout,
        width,
        length,
        cableCount
    );

    // Flat houses with a layout footprint: regenerate from the uniform (count+1) grid so
    // saved TopPointX/Z (often middle-half inset or wrong-axis) cannot leave cables bunched.
    const preferLayoutGrid =
        layoutBin &&
        !layoutBin.isRound &&
        grid &&
        cableNum != null &&
        width > 0 &&
        length > 0 &&
        (isThreeTen ||
            binLooksLikeFlatHouse(bin, layoutBin) ||
            !!flatHouseLayoutFootprintDimensions(layoutBin, site));

    if (preferLayoutGrid) {
        const fromGrid = topPointFromFlatHouseCableLayout(
            cableNum,
            bin.cableLayout,
            width,
            length,
            cableCount
        );
        if (fromGrid) return fromGrid;
    }

    const dbX = Number(cable.topPointX);
    const dbZ = Number(cable.topPointZ);
    if (isMeaningfulCableTopPoint(dbX, dbZ)) {
        const fromDb = normalizeBtsTopPoint(
            { topPointX: dbX, topPointZ: dbZ },
            layoutBin,
            bin,
            site
        );
        if (fromDb) return fromDb;
    }

    if (grid && cableNum != null && width > 0 && length > 0) {
        const fromGrid = topPointFromFlatHouseCableLayout(
            cableNum,
            bin.cableLayout,
            width,
            length,
            cableCount
        );
        if (fromGrid) {
            const normGrid = normalizeBtsTopPoint(fromGrid, layoutBin, bin, site);
            if (normGrid) return normGrid;
            if (!layoutBin?.isRound) return fromGrid;
        }
    }

    const skipAngleSlot = isThreeTen;
    if (!skipAngleSlot) {
        const fromAngle = topPointFromBtsAnglePosition(
            btsCableGridSlot(cable),
            width,
            length,
            bin.cableLayout
        );
        if (fromAngle) {
            const normAngle = normalizeBtsTopPoint(
                fromAngle,
                layoutBin,
                bin,
                site
            );
            if (normAngle) return normAngle;
            if (roundTopPointFitsBin(fromAngle, layoutBin, site)) return fromAngle;
            if (!layoutBin?.isRound) return fromAngle;
        }
    }

    return null;
};

/**
 * Place cable n directly in the wall polygon frame (u along Length / bunks, v along Width).
 * Avoids catalog→rotate drift when layout sides and BTS W/L disagree.
 */
export const topPointFromOrientedFlatFootprint = (
    cableNumber,
    oriented,
    cableLayout,
    cableCount = null
) => {
    if (!oriented) return null;
    const w = Number(oriented.width) || 0;
    const l = Number(oriented.length) || 0;
    if (!(w > 0 && l > 0)) return null;

    const grid = flatHouseGridFromCableLayout(cableLayout, w, l, cableCount);
    if (!grid) return null;

    // Oriented footprint is always BTS Width×Length (short×long).
    const cablesPerBunk = grid.cols;
    const bunks = grid.rows;
    const n = Math.round(Number(cableNumber));
    if (!Number.isFinite(n) || n < 1 || n > cablesPerBunk * bunks) return null;

    const onBunk = (n - 1) % cablesPerBunk;
    const bunk = Math.floor((n - 1) / cablesPerBunk);
    const vSpacing = w / (cablesPerBunk + 1);
    const uSpacing = l / (bunks + 1);
    // Match catalog SW grid: west −Width, south +Length for bunk 0 / onBunk 0.
    const v = -w / 2 + (onBunk + 1) * vSpacing;
    const u = l / 2 - (bunk + 1) * uSpacing;

    const cos = Math.cos(oriented.angleRad);
    const sin = Math.sin(oriented.angleRad);
    // lengthDir=(cos,sin), widthDir=(-sin,cos)
    return {
        topPointX: oriented.cx + u * cos - v * sin,
        topPointZ: oriented.cz + u * sin + v * cos,
    };
};

/**
 * Map catalog-frame cable XZ (Width on X, Length on Z) into the oriented wall footprint.
 * Also handles legacy Lincoln scale-from-canonical when no oriented poly exists.
 */
export const mapFlatCablePointToWallFootprint = (topPoint, layoutBin, site = null) => {
    if (!topPoint || !layoutBin || layoutBin.isRound) return topPoint;
    // Points from topPointFromOrientedFlatFootprint are already wall-local.
    if (topPoint.wallLocal) {
        return {
            topPointX: topPoint.topPointX,
            topPointZ: topPoint.topPointZ,
        };
    }

    const oriented = orientedLayoutFootprintFt(layoutBin, site);
    if (oriented) {
        // Catalog Length runs on +Z (angle π/2). Rotate so Length aligns with oriented.angleRad.
        const theta = oriented.angleRad - Math.PI / 2;
        const cos = Math.cos(theta);
        const sin = Math.sin(theta);
        const x = Number(topPoint.topPointX) || 0;
        const z = Number(topPoint.topPointZ) || 0;
        return {
            topPointX: x * cos - z * sin + oriented.cx,
            topPointZ: x * sin + z * cos + oriented.cz,
        };
    }

    if (!flatBinUsesCatalogCableScale(layoutBin, site)) return topPoint;

    const bin = linkedBinForLayoutBin(layoutBin, site);
    const { width: gridW, length: gridL } = flatHouseCableGridDimensions(
        bin,
        layoutBin,
        site
    );
    const { width: catW, length: catL } = CANONICAL_FLAT_HOUSE_CATALOG_FT;

    // Grid already computed in layout footprint feet — skip second scale pass.
    if (
        Math.abs(gridW - catW) > 1 ||
        Math.abs(gridL - catL) > 1
    ) {
        return topPoint;
    }

    const { spanX, spanZ } = layoutFootprintSpanFt(layoutBin, site);
    const layW = spanX > 0 ? spanX : Number(layoutBin.width ?? 0);
    const layL = spanZ > 0 ? spanZ : Number(layoutBin.length ?? 0);
    if (!(layW > 0 && layL > 0 && catW > 0 && catL > 0)) return topPoint;

    const { topPointX, topPointZ } = topPoint;
    return {
        topPointX: topPointX * (layW / catW),
        topPointZ: topPointZ * (layL / catL),
    };
};

const findLayoutBinForLayoutCable = (site, layoutCable, bin) => {
    const lbs = site?.layoutBins || [];
    if (layoutCable?.customerSiteLayoutBinId != null) {
        const hit = lbs.find(
            (lb) =>
                lb.customerSiteLayoutBinId ===
                layoutCable.customerSiteLayoutBinId
        );
        if (hit) return hit;
    }
    if (bin) {
        const hit = lbs.find(
            (lb) =>
                sameBinDataIdLoose(lb.binId, bin.binDataId) ||
                sameBinDataIdLoose(lb.binId, bin.binId)
        );
        if (hit) return hit;
    }
    const cableBinName = String(layoutCable?.cableName || "")
        .replace(/[-\s]*\d+\s*$/, "")
        .trim();
    if (!cableBinName) return null;
    const norm = normalizeCableLookupName(cableBinName);
    const byName =
        lbs.find((lb) => {
            const bn = String(lb.binName || "").trim();
            return (
                bn === cableBinName ||
                normalizeCableLookupName(bn) === norm
            );
        }) ?? null;
    if (byName) return byName;

    // Crittenden: cables named "S-12" / "B-3" while layout bins are "Small House" / "Big House".
    if (norm.length >= 1 && norm.length <= 3) {
        const prefixHits = lbs.filter((lb) => {
            const bn = normalizeCableLookupName(lb.binName);
            return bn.startsWith(norm);
        });
        if (prefixHits.length === 1) return prefixHits[0];
    }
    return null;
};

const normalizeCableLookupName = (name) =>
    String(name || "").toLowerCase().replace(/\s+/g, "");

/** Resolve display XZ for a persisted layout cable row (render-time fallback). */
export const resolveLayoutCableTopPoint = (layoutCable, site, layoutBinHint = null) => {
    if (!layoutCable || !site) return null;
    if (layoutCable.aframe) return null;

    const layoutBinEarly =
        layoutBinHint ??
        findLayoutBinForLayoutCable(site, layoutCable, null) ??
        (site.layoutBins || []).find(
            (lb) =>
                lb.customerSiteLayoutBinId ===
                layoutCable.customerSiteLayoutBinId
        );

    const finish = (pt) => {
        if (!pt) return null;
        const layoutBinResolved =
            layoutBinEarly ??
            findLayoutBinForLayoutCable(site, layoutCable, null);
        if (!layoutBinResolved) return pt;
        return mapFlatCablePointToWallFootprint(pt, layoutBinResolved, site);
    };

    const linkedBinEarly = layoutBinEarly
        ? linkedBinForLayoutBin(layoutBinEarly, site)
        : null;
    // Always rebuild flat grids from the wall footprint. Stale TopPointX/Z (wrong frame
    // or sibling-bin scale) is what left Crittenden Small House cables outside the walls.
    const preferBtsCableLayoutGrid =
        layoutBinEarly &&
        !layoutBinEarly.isRound &&
        !!flatHouseLayoutFootprintDimensions(layoutBinEarly, site);

    const storedX = layoutCable.topPointX ?? layoutCable.x;
    const storedZ = layoutCable.topPointZ ?? layoutCable.z;
    if (
        !preferBtsCableLayoutGrid &&
        layoutBinEarly &&
        !binIsRoundSilo(linkedBinEarly, layoutBinEarly) &&
        isMeaningfulCableTopPoint(storedX, storedZ)
    ) {
        const fromStored = normalizeCableTopPointToBinLocal(
            storedX,
            storedZ,
            layoutBinEarly,
            site
        );
        if (fromStored) return finish(fromStored);
    }

    const cableNum = cableNumberFromCableRecord({
        name: layoutCable.cableName,
        cableName: layoutCable.cableName,
        // Do not pass binOrder as number — it is site-wide, not per-bin (S-1 → 79).
    });

    const layoutBin =
        layoutBinEarly ??
        findLayoutBinForLayoutCable(site, layoutCable, null);

    const bins = site.bins || [];
    let bin = null;
    if (layoutBin?.binId != null) {
        bin = bins.find(
            (b) =>
                sameBinDataIdLoose(b.binDataId, layoutBin.binId) ||
                sameBinDataIdLoose(b.binId, layoutBin.binId)
        );
    }
    if (!bin && layoutCable.binDataId != null) {
        bin = bins.find(
            (b) =>
                sameBinDataIdLoose(b.binDataId, layoutCable.binDataId) ||
                sameBinDataIdLoose(b.binId, layoutCable.binDataId)
        );
    }
    if (!bin && layoutCable.binId != null) {
        bin = bins.find(
            (b) =>
                sameBinDataIdLoose(b.binDataId, layoutCable.binId) ||
                sameBinDataIdLoose(b.binId, layoutCable.binId)
        );
    }
    if (!bin && layoutBin?.binName) {
        const name = String(layoutBin.binName).trim();
        bin = bins.find(
            (b) =>
                String(b.binName || "").trim() === name ||
                String(b.binName || "").replace(/\s+/g, "") ===
                    name.replace(/\s+/g, "")
        );
    }

    const layoutBinResolved =
        layoutBin ?? findLayoutBinForLayoutCable(site, layoutCable, bin);

    if (bin) {
        const cable =
            (bin.cableList || []).find(
                (c) =>
                    layoutCable.cableName &&
                    c.name &&
                    normalizeCableLookupName(c.name) ===
                        normalizeCableLookupName(layoutCable.cableName)
            ) ??
            (layoutCable.cableId != null
                ? (bin.cableList || []).find(
                      (c) => Number(c.id) === Number(layoutCable.cableId)
                  )
                : null) ?? {
                name: layoutCable.cableName,
                cableName: layoutCable.cableName,
            };
        const fromBin = resolveBtsCableTopPoint(
            cable,
            bin,
            layoutBinResolved,
            site
        );
        if (fromBin) return finish(fromBin);
    }

    if (cableNum != null && layoutBinResolved && !layoutBinResolved.isRound) {
        const { width, length } = flatHouseCableGridDimensions(
            bin,
            layoutBinResolved,
            site
        );
        const grid = flatHouseGridFromCableLayout(
            bin?.cableLayout ?? "3-10",
            width,
            length,
            layoutBinFlatCableCount(layoutBinResolved, site) ||
                bin?.cableList?.length ||
                0
        );
        if (grid && width > 0 && length > 0) {
            const fromGrid = topPointFromFlatHouseCableLayout(
                cableNum,
                bin?.cableLayout ?? "3-10",
                width,
                length,
                layoutBinFlatCableCount(layoutBinResolved, site) ||
                    bin?.cableList?.length ||
                    0
            );
            if (fromGrid) return finish(fromGrid);
        }
    }

    return null;
};

/**
 * BTS cableList is catalog-axis-aligned; saved flat sides bake in the full orientation.
 * Editor walls aren't spun by bin3dModel, so we rotate the cable grid by the angle between
 * the baked footprint's major axis and the catalog footprint's major axis (PCA, tessellation
 * robust). layoutBin.rotation is unreliable (often 0 for curved/arc flats), so geometry wins.
 */
export const flatCableRadiansToNorth = (layoutBin, site = null) => {
    // Cable XZ is mapped into the wall polygon frame in mapFlatCablePointToWallFootprint.
    if (orientedLayoutFootprintFt(layoutBin, site)) return null;
    // Lincoln-style long-on-X flats: cable XZ already scaled to layout walls — no extra spin.
    if (flatBinUsesCatalogCableScale(layoutBin, site)) return null;

    // Case A: the footprint geometry is genuinely rotated in the editor (rotation baked into
    // the vertices, walls drawn from those sides, no degreesToNorth spin). Match that angle.
    const poly = layoutBinFootprintPoly(layoutBin, site);
    const polyAxis = flatSideAngleRadFromPolygon(poly);
    const catalogAxis = catalogMajorAxisAngleRad(layoutBin, site);
    if (polyAxis != null && catalogAxis != null) {
        const delta = wrapAxisDeltaRad(polyAxis - catalogAxis);
        if (Math.abs(delta) > 0.02) {
            return delta;
        }
    }

    // Case B: footprint is axis-aligned (un-rotated). The walls are oriented by the catalog
    // degreesToNorth spin, so cables must use the same production rotation to stay parallel.
    let deg =
        layoutBin?.bin?.degreesToNorth ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        )?.degreesToNorth;
    if (deg == null && layoutBin?.rotation != null && Number.isFinite(layoutBin.rotation)) {
        deg = layoutBin.rotation;
    }
    const base = btsCableRadiansFromDeg(deg);
    const correction = flatBinProductionRotationCorrectionRad(layoutBin, site);
    if (base == null) return correction || null;
    return base + correction;
};

/** Longest-edge angle from saved side rows (bin-local feet). */
export const flatRotationDegFromSidePieces = (sides) => {
    const rad = flatSideAngleRadFromPolygon(
        (sides || []).flatMap((s) => [
            { x: s.x1, z: s.z1 },
            { x: s.x2, z: s.z2 },
        ])
    );
    if (rad == null) return null;
    const deg = (-rad * 180) / Math.PI;
    return round1(((deg % 360) + 360) % 360);
};

/**
 * Editor-drawn flat → degreesToNorth for layout tables (load round-trip / legacy walls).
 */
export const flatRotationDegFromEditor = (segments, face = null) => {
    let rectId = null;
    if (face) {
        rectId =
            closedRectIdForFace(face, segments) ||
            (String(face.id).startsWith("flat:")
                ? face.id.slice(5)
                : null);
    }
    if (!rectId) {
        const seg = (segments || []).find((s) => s.closedRectId);
        rectId = seg?.closedRectId ?? null;
    }
    if (!rectId) return null;
    const oriented = getFlatRectOrientedGeometry(segments, rectId);
    if (!oriented) return null;
    const deg = (-oriented.angleRad * 180) / Math.PI;
    return round1(((deg % 360) + 360) % 360);
};

/** Map catalog width/length (ft) to half-axes along the flat's current orientation. */
export const orientedHalfAxesFromBinFt = (oriented, widthFt, lengthFt, feetPerPixel) => {
    if (!oriented || feetPerPixel <= 0) return null;
    let halfW = widthFt / 2 / feetPerPixel;
    let halfL = lengthFt / 2 / feetPerPixel;
    const curW = oriented.halfW * 2 * feetPerPixel;
    const curL = oriented.halfL * 2 * feetPerPixel;
    const direct = Math.abs(curW - widthFt) + Math.abs(curL - lengthFt);
    const swapped = Math.abs(curL - widthFt) + Math.abs(curW - lengthFt);
    if (swapped + 0.05 < direct) {
        const t = halfW;
        halfW = halfL;
        halfL = t;
    }
    return { halfW, halfL };
};

/** Four walls forming a flat house at center/size/angle (image space). */
export const createFlatRectangleSegmentsAtOriented = (
    cx,
    cy,
    halfW,
    halfL,
    angleRad,
    rectIdIn = null
) => {
    if (halfW * 2 < MIN_FLAT_RECT_IMAGE_PX || halfL * 2 < MIN_FLAT_RECT_IMAGE_PX) {
        return [];
    }
    const rectId = rectIdIn || newSegmentId();
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const toGlobal = (u, v) => ({
        x: cx + u * cos - v * sin,
        y: cy + u * sin + v * cos,
    });
    const corners = [
        toGlobal(-halfW, -halfL),
        toGlobal(halfW, -halfL),
        toGlobal(halfW, halfL),
        toGlobal(-halfW, halfL),
    ];
    const wall = (i, j) => ({
        id: newSegmentId(),
        kind: "wall",
        closedRectId: rectId,
        x1: corners[i].x,
        y1: corners[i].y,
        x2: corners[j].x,
        y2: corners[j].y,
        isInner: false,
    });
    return [wall(0, 1), wall(1, 2), wall(2, 3), wall(3, 0)];
};

/** Replace flat walls while preserving closedRectId and orientation. */
export const replaceFlatRectSegmentsOriented = (
    segments,
    rectId,
    cx,
    cy,
    halfW,
    halfL,
    angleRad
) => {
    const rest = segments.filter((s) => s.closedRectId !== rectId);
    const next = createFlatRectangleSegmentsAtOriented(
        cx,
        cy,
        halfW,
        halfL,
        angleRad,
        rectId
    );
    if (!next.length) return segments;
    return [...rest, ...next];
};

/** Replace the four walls of an existing flat house (keeps closedRectId). */
export const replaceFlatRectSegments = (segments, rectId, x1, y1, x2, y2) => {
    const rest = segments.filter((s) => s.closedRectId !== rectId);
    const next = createFlatRectangleSegments(x1, y1, x2, y2, rectId);
    if (!next.length) return segments;
    return [...rest, ...next];
};

/** Center + size in feet → corner coords for createFlatRectangleSegments. */
export const flatRectCornersFromCenter = (
    cx,
    cy,
    widthFt,
    lengthFt,
    feetPerPixel
) => {
    const wFt = Math.max(Number(widthFt) || 30, 1);
    const lFt = Math.max(Number(lengthFt) || 30, 1);
    let halfW;
    let halfL;
    if (feetPerPixel > 0) {
        halfW = (wFt / 2) / feetPerPixel;
        halfL = (lFt / 2) / feetPerPixel;
    } else {
        halfW = DEFAULT_MARKER_RADIUS_PX;
        halfL = DEFAULT_MARKER_RADIUS_PX;
    }
    return {
        x1: cx - halfW,
        y1: cy - halfL,
        x2: cx + halfW,
        y2: cy + halfL,
    };
};

/** Four axis-aligned walls forming a rectangular flat house. */
export const createFlatRectangleSegments = (x1, y1, x2, y2, rectIdIn = null) => {
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    if (maxX - minX < MIN_FLAT_RECT_IMAGE_PX || maxY - minY < MIN_FLAT_RECT_IMAGE_PX) {
        return [];
    }
    const rectId = rectIdIn || newSegmentId();
    const wall = (xA, yA, xB, yB) => ({
        id: newSegmentId(),
        kind: "wall",
        closedRectId: rectId,
        x1: xA,
        y1: yA,
        x2: xB,
        y2: yB,
        isInner: false,
    });
    return [
        wall(minX, minY, maxX, minY),
        wall(maxX, minY, maxX, maxY),
        wall(maxX, maxY, minX, maxY),
        wall(minX, maxY, minX, minY),
    ];
};

/** Unanchored or broken segment reference — not part of enclosure graph. */
export const isFloatingSegment = (seg, markers, segments) => {
    if (isClosedRectSegment(seg)) {
        return !resolveSegmentGeometry(seg, markers, segments);
    }
    if (segmentIsFullySnapped(seg)) {
        return !resolveSegmentGeometry(seg, markers, segments);
    }
    /** Restored (or placed) walls/arcs with explicit endpoints — keep even without bin snap ids. */
    if (
        (seg.kind === "wall" || seg.kind === "arc") &&
        seg.x1 != null &&
        seg.x2 != null
    ) {
        return false;
    }
    return true;
};

const applySnapToEnd = (seg, end, snap) => {
    if (!snap) return;
    delete seg[`attach${end}`];
    delete seg[`markerId${end}`];
    delete seg[`angle${end}`];
    if (snap.kind === "segment-mid") {
        seg[`attach${end}`] = {
            segmentId: snap.segmentId,
            role: "mid",
            t: snap.t ?? 0.5,
            x: snap.x,
            y: snap.y,
        };
        return;
    }
    if (snap.kind === "segment-end") {
        seg[`attach${end}`] = {
            segmentId: snap.segmentId,
            role: snap.end === "start" ? "start" : "end",
        };
        return;
    }
    seg[`markerId${end}`] = snap.markerId;
    seg[`angle${end}`] = snap.angle;
};

/** Wall or arc between layout snap points (bin edge, wall end, or wall midpoint). */
export const createSegmentBetweenSnaps = (fromSnap, toSnap, kind, bulgePoint) => {
    if (!fromSnap || !toSnap) return null;
    const seg = { id: newSegmentId(), kind, isInner: false };
    applySnapToEnd(seg, 1, fromSnap);
    applySnapToEnd(seg, 2, toSnap);
    if (!segmentIsFullySnapped(seg)) return null;
    if (kind === "arc" && bulgePoint) {
        seg.bulgeX = bulgePoint.x;
        seg.bulgeY = bulgePoint.y;
        seg.isInner = arcIsInnerFromBulge(
            fromSnap.x,
            fromSnap.y,
            toSnap.x,
            toSnap.y,
            bulgePoint.x,
            bulgePoint.y
        );
    }
    return seg;
};

/** @deprecated use createSegmentBetweenSnaps */
export const createSegmentBetweenBins = (fromSnap, toSnap, kind, bulgePoint) =>
    createSegmentBetweenSnaps(fromSnap, toSnap, kind, bulgePoint);



/** Centroid of resolved segment endpoints (image px). */

export const sticeCentroid = (segments, markers, allSegments) => {

    if (!segments?.length) return { x: 0, y: 0 };

    const segmentList = allSegments || segments;

    let sx = 0;

    let sy = 0;

    let n = 0;

    segments.forEach((s) => {

        const g = resolveSegmentGeometry(s, markers, segmentList);

        if (!g) return;

        sx += g.x1 + g.x2;

        sy += g.y1 + g.y2;

        n += 2;

        if (g.kind === "arc" && g.curveX != null) {

            sx += g.curveX;

            sy += g.curveY;

            n += 1;

        }

    });

    return n > 0 ? { x: sx / n, y: sy / n } : { x: 0, y: 0 };

};



/** Circumcircle through three points (image space). */

export const circleFromThreePoints = (x1, y1, x2, y2, x3, y3) => {

    const d = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2));

    if (Math.abs(d) < 1e-8) return null;

    const x1sq = x1 * x1 + y1 * y1;

    const x2sq = x2 * x2 + y2 * y2;

    const x3sq = x3 * x3 + y3 * y3;

    const ux = (x1sq * (y2 - y3) + x2sq * (y3 - y1) + x3sq * (y1 - y2)) / d;

    const uy = (x1sq * (x3 - x2) + x2sq * (x1 - x3) + x3sq * (x2 - x1)) / d;

    const r = Math.hypot(x1 - ux, y1 - uy);

    if (r < 1e-6) return null;

    return { cx: ux, cy: uy, r };

};



/** Bulge side: positive = left of A→B (CCW outer for typical stice). */

export const arcIsInnerFromBulge = (x1, y1, x2, y2, bx, by) => {

    const cross = (x2 - x1) * (by - y1) - (y2 - y1) * (bx - x1);

    return cross < 0;

};



const round1 = (v) => Math.round(v * 10) / 10;

const FACE_ENDPOINT_TOL_PX = 4;

const endpointsMatchPx = (x1, y1, x2, y2, tol = FACE_ENDPOINT_TOL_PX) =>
    Math.hypot(x1 - x2, y1 - y2) <= tol;

/** Reverse a resolved segment so chain direction matches the face perimeter walk. */
const flipSegmentGeometry = (g) => {
    if (!g) return g;
    if (g.kind !== "arc" || g.curveX == null || g.curveY == null) {
        return { ...g, x1: g.x2, y1: g.y2, x2: g.x1, y2: g.y1 };
    }
    return {
        ...g,
        x1: g.x2,
        y1: g.y2,
        x2: g.x1,
        y2: g.y1,
        isInner: arcIsInnerFromBulge(g.x2, g.y2, g.x1, g.y1, g.curveX, g.curveY),
    };
};

/**
 * Order walls/arcs into one continuous perimeter chain (image px).
 * SticeArcWall extrudes a single closed loop — order must match the enclosed face.
 */
export const orderFaceSegmentsLoop = (segmentIds, segments, markers) => {
    const segmentList = segments;
    const entries = [];
    for (const id of segmentIds || []) {
        const raw = segmentList.find((s) => s.id === id);
        if (!raw) continue;
        const g = resolveSegmentGeometry(raw, markers, segmentList);
        if (!g) continue;
        entries.push({ raw, g });
    }
    if (entries.length === 0) return [];
    if (entries.length === 1) {
        return [{ segment: entries[0].raw, reversed: false }];
    }

    const used = new Set();
    const ordered = [];
    let current = entries[0];
    used.add(current.raw.id);
    ordered.push({ segment: current.raw, reversed: false });
    let endX = current.g.x2;
    let endY = current.g.y2;

    while (ordered.length < entries.length) {
        let next = null;
        let reversed = false;
        for (const cand of entries) {
            if (used.has(cand.raw.id)) continue;
            if (endpointsMatchPx(endX, endY, cand.g.x1, cand.g.y1)) {
                next = cand;
                break;
            }
            if (endpointsMatchPx(endX, endY, cand.g.x2, cand.g.y2)) {
                next = cand;
                reversed = true;
                break;
            }
        }
        if (!next) break;
        used.add(next.raw.id);
        ordered.push({ segment: next.raw, reversed });
        endX = reversed ? next.g.x1 : next.g.x2;
        endY = reversed ? next.g.y1 : next.g.y2;
    }
    return ordered;
};

/** True when ordered segments form a closed loop (for 3D stice extrusion). */
export const faceSegmentLoopIsClosed = (ordered, markers, allSegments) => {
    if (!ordered?.length || ordered.length < 3) return false;
    const firstG = resolveSegmentGeometry(ordered[0].segment, markers, allSegments);
    const last = ordered[ordered.length - 1];
    const lastG = resolveSegmentGeometry(last.segment, markers, allSegments);
    if (!firstG || !lastG) return false;
    const startX = ordered[0].reversed ? firstG.x2 : firstG.x1;
    const startY = ordered[0].reversed ? firstG.y2 : firstG.y1;
    const endX = last.reversed ? lastG.x1 : lastG.x2;
    const endY = last.reversed ? lastG.y1 : lastG.y2;
    return endpointsMatchPx(endX, endY, startX, startY, FACE_ENDPOINT_TOL_PX * 2);
};

/** Image px → feet offset from stice center (layout bin-local x/z). */

const pxToLocalFt = (px, py, centerPx, feetPerPixel) => ({

    x: round1((px - centerPx.x) * feetPerPixel),

    z: round1((py - centerPx.y) * feetPerPixel),

});



/**

 * One wall or arc segment → CustomerSiteLayoutSide draft row (bin-local feet, stice-centered).
 * Persist these in the DB; convert to group-local feet when loading into selectedSite for drawSite().

 * curveRadius 0 = straight wall.

 */

const sameBinDataIdStice = (a, b) => {
    if (a === undefined || a === null || b === undefined || b === null) return false;
    const na = parseInt(String(a), 10);
    const nb = parseInt(String(b), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
    return String(a) === String(b);
};

/** Round-bin radius in feet — catalog/BTS by default; editor footprint when requested. */
export const getCatalogBinRadiusFt = (
    marker,
    siteBin,
    feetPerPixel,
    options = {}
) => {
    const preferEditorFootprint = options.preferEditorFootprint === true;
    if (preferEditorFootprint && marker?.radiusPx > 4 && feetPerPixel > 0) {
        return marker.radiusPx * feetPerPixel;
    }
    if (siteBin) {
        const d =
            (siteBin.diameter > 0 ? siteBin.diameter : 0) ||
            (siteBin.width > 0 ? siteBin.width : 0);
        if (d > 0) return d / 2;
    }
    if (marker?.radiusPx > 4 && feetPerPixel > 0) {
        return marker.radiusPx * feetPerPixel;
    }
    return DEFAULT_MARKER_RADIUS_PX * (feetPerPixel || PIXEL_TO_FEET_DEFAULT);
};

/**
 * Bin perimeter arc → layout side in stice-local feet (CustomerSiteLayoutSide before save).
 * Uses surrounding bin center + catalog radius — same model as persisted layout tables / Site 3D.
 * binArcKey: `${markerId}:${angle1}-${angle2}` (radians).
 */
export const binArcKeyToSideDraft = (
    binArcKey,
    markers,
    centerPx,
    feetPerPixel,
    siteBins = null,
    useEditorFootprint = false
) => {
    const colonIdx = binArcKey.indexOf(":");
    if (colonIdx < 0) return null;
    const markerId = binArcKey.slice(0, colonIdx);
    const anglePart = binArcKey.slice(colonIdx + 1);
    const dash = anglePart.lastIndexOf("-");
    if (dash < 0) return null;
    const a1 = parseFloat(anglePart.slice(0, dash));
    const a2 = parseFloat(anglePart.slice(dash + 1));
    const m = markers.find((mk) => mk.id === markerId);
    if (!m || !Number.isFinite(a1) || !Number.isFinite(a2)) return null;

    const siteBin = (siteBins || []).find((b) =>
        sameBinDataIdStice(b.binDataId, m.binDataId)
    );
    const r = getCatalogBinRadiusFt(m, siteBin, feetPerPixel, {
        preferEditorFootprint: useEditorFootprint,
    });
    const bx = (m.x - centerPx.x) * feetPerPixel;
    const bz = (m.y - centerPx.y) * feetPerPixel;

    let delta = a2 - a1;
    while (delta <= 0) delta += 2 * Math.PI;
    const midA = a1 + delta / 2;

    return {
        x1: round1(bx + r * Math.cos(a1)),
        y1: 0,
        z1: round1(bz + r * Math.sin(a1)),
        x2: round1(bx + r * Math.cos(a2)),
        y2: 0,
        z2: round1(bz + r * Math.sin(a2)),
        curveX: round1(bx + r * Math.cos(midA)),
        curveY: 0,
        curveZ: round1(bz + r * Math.sin(midA)),
        curveRadius: round1(r),
        isInner: true,
        isSiteRender: true,
    };
};

/**
 * Walls + bin perimeter arcs → chained layout sides (bin-local feet) for layout table proxy.
 */
export const buildSticeSidesForLayoutTables = (
    stice,
    face,
    markers,
    segmentList,
    centerPx,
    feetPerPixel,
    siteBins = null,
    useEditorFootprint = false
) => {
    const binArcKeys = face?.binArcKeys || stice?.binArcKeys || [];
    const hasSegments = (stice?.segmentIds?.length || stice?.segments?.length) > 0;
    const hasBinArcs = binArcKeys.length > 0;
    if (!hasSegments && !hasBinArcs) return [];

    let ordered = (stice?.segments || []).map((s) => ({ segment: s, reversed: false }));
    if (stice?.segmentIds?.length) {
        ordered = orderFaceSegmentsLoop(stice.segmentIds, segmentList, markers);
    }

    const sidePieces = [];
    ordered.forEach(({ segment, reversed }) => {
        const g = resolveSegmentGeometry(segment, markers, segmentList);
        if (!g) return;
        const geom = reversed ? flipSegmentGeometry(g) : g;
        sidePieces.push(segmentToSideDraft(geom, centerPx, feetPerPixel));
    });
    binArcKeys.forEach((key) => {
        const arcSide = binArcKeyToSideDraft(
            key,
            markers,
            centerPx,
            feetPerPixel,
            siteBins,
            useEditorFootprint
        );
        if (arcSide) sidePieces.push(arcSide);
    });
    if (!sidePieces.length) return [];

    const chainTol = Math.max(0.75, (feetPerPixel || 0.1) * 6);
    return chainSideDraftsByEndpoints(sidePieces, chainTol);
};

/** Closed boundary polyline (bin-local ft) sampled from chained layout sides. */
export const polygonFtFromChainedSides = (chained) => {
    const pts = [];
    (chained || []).forEach((side) => {
        if (side.curveRadius > 0) {
            sampleSideArcPoints(side).forEach((p) => pushPolyPoint(pts, p.x, p.z));
        } else {
            pushPolyPoint(pts, side.x1, side.z1);
        }
    });
    return pts;
};

const pointInPolygonPx = (x, y, poly) => {
    if (!poly?.length) return false;
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x;
        const yi = poly[i].y;
        const xj = poly[j].x;
        const yj = poly[j].y;
        if (
            yi > y !== yj > y &&
            x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-9) + xi
        ) {
            inside = !inside;
        }
    }
    return inside;
};

/** Stice center on a round-bin interior (not a valid pocket between bins). */
export const sticeCenterInsideCatalogBin = (
    centerPx,
    groupCenterPx,
    markers,
    siteBins,
    feetPerPixel,
    face = null,
    useEditorFootprint = false
) => {
    if (face?.polygon?.length >= 3 && pointInPolygonPx(centerPx.x, centerPx.y, face.polygon)) {
        return false;
    }
    const gx = groupCenterPx.centerX ?? groupCenterPx.x ?? 0;
    const gy = groupCenterPx.centerY ?? groupCenterPx.y ?? 0;
    const sx = (centerPx.x - gx) * feetPerPixel;
    const sz = (centerPx.y - gy) * feetPerPixel;
    for (const m of markers || []) {
        const siteBin = (siteBins || []).find((b) =>
            sameBinDataIdStice(b.binDataId, m.binDataId)
        );
        const r = getCatalogBinRadiusFt(m, siteBin, feetPerPixel, {
            preferEditorFootprint: useEditorFootprint,
        });
        const bx = (m.x - gx) * feetPerPixel;
        const bz = (m.y - gy) * feetPerPixel;
        if (Math.hypot(sx - bx, sz - bz) < r * 0.45) return true;
    }
    return false;
};

const finiteFt = (n) => typeof n === "number" && Number.isFinite(n);

/** Chain tolerance from layout-side world/group feet (x1/z1). */
export const chainTolForLayoutSideWorld = (sides) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    (sides || []).forEach((s) => {
        [s.x1, s.x2].forEach((x) => {
            if (finiteFt(x)) {
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
            }
        });
        [s.z1, s.z2].forEach((z) => {
            if (finiteFt(z)) {
                minZ = Math.min(minZ, z);
                maxZ = Math.max(maxZ, z);
            }
        });
    });
    const span = Math.max(maxX - minX, maxZ - minZ, 1);
    return Math.max(1, span * 0.04);
};

/**
 * Order layout sides into a perimeter loop before drawSite corner math.
 * Mutates endpoint fields when a segment must be reversed to chain.
 */
export const orderLayoutSideRefsByEndpoints = (sides, tolFt = 1) => {
    if (!sides?.length || sides.length < 2) return sides;

    const near = (x1, z1, x2, z2) => Math.hypot(x1 - x2, z1 - z2) <= tolFt;
    const swapEndpoints = (s) => {
        const x1 = s.x1;
        const z1 = s.z1;
        const y1 = s.y1;
        s.x1 = s.x2;
        s.z1 = s.z2;
        s.y1 = s.y2;
        s.x2 = x1;
        s.z2 = z1;
        s.y2 = y1;
    };

    const pool = sides.slice();
    const ordered = [pool.shift()];
    let endX = ordered[0].x2;
    let endZ = ordered[0].z2;

    while (pool.length > 0) {
        let idx = -1;
        let reversed = false;
        for (let i = 0; i < pool.length; i++) {
            const s = pool[i];
            if (near(endX, endZ, s.x1, s.z1)) {
                idx = i;
                break;
            }
            if (near(endX, endZ, s.x2, s.z2)) {
                idx = i;
                reversed = true;
                break;
            }
        }
        if (idx < 0) break;
        const next = pool.splice(idx, 1)[0];
        if (reversed) swapEndpoints(next);
        ordered.push(next);
        endX = next.x2;
        endZ = next.z2;
    }

    return ordered.length >= sides.length ? ordered : sides;
};

/** Chain layout-side rows by lx/lz endpoints (feet, after drawSite). */
export const chainLayoutSidesLocal = (sides, tolFt = 1) => {
    if (!sides?.length) return [];
    if (sides.length === 1) return sides;

    const near = (x1, z1, x2, z2) => Math.hypot(x1 - x2, z1 - z2) <= tolFt;
    const swapSide = (s) => ({
        ...s,
        lx1: s.lx2,
        lz1: s.lz2,
        lx2: s.lx1,
        lz2: s.lz1,
        hlx1: s.hlx2,
        hlz1: s.hlz2,
        hlx2: s.hlx1,
        hlz2: s.hlz1,
    });

    const pool = sides.map((s) => ({ ...s }));
    const ordered = [pool.shift()];
    let endX = ordered[0].lx2;
    let endZ = ordered[0].lz2;

    while (pool.length > 0) {
        let idx = -1;
        let reversed = false;
        for (let i = 0; i < pool.length; i++) {
            const s = pool[i];
            if (near(endX, endZ, s.lx1, s.lz1)) {
                idx = i;
                break;
            }
            if (near(endX, endZ, s.lx2, s.lz2)) {
                idx = i;
                reversed = true;
                break;
            }
        }
        if (idx < 0) break;
        let next = pool.splice(idx, 1)[0];
        if (reversed) next = swapSide(next);
        ordered.push(next);
        endX = next.lx2;
        endZ = next.lz2;
    }

    return ordered.length >= 2 ? ordered : sides;
};

/** Adaptive chain tolerance from side span (feet). */
export const chainTolForLayoutSides = (sides) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    (sides || []).forEach((s) => {
        [s.lx1, s.lx2].forEach((x) => {
            if (typeof x === "number" && Number.isFinite(x)) {
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
            }
        });
        [s.lz1, s.lz2].forEach((z) => {
            if (typeof z === "number" && Number.isFinite(z)) {
                minZ = Math.min(minZ, z);
                maxZ = Math.max(maxZ, z);
            }
        });
    });
    const span = Math.max(maxX - minX, maxZ - minZ, 1);
    return Math.max(1, span * 0.04);
};

/** Chain bin-local side rows into one perimeter loop (feet). */
export const chainSideDraftsByEndpoints = (sides, tolFt = 0.75) => {
    if (!sides?.length) return [];
    if (sides.length === 1) return sides;

    const swapSide = (s) => ({
        ...s,
        x1: s.x2,
        z1: s.z2,
        x2: s.x1,
        z2: s.z1,
        curveX: s.curveX,
        curveZ: s.curveZ,
        curveRadius: s.curveRadius,
        isInner: s.isInner,
    });

    const near = (x1, z1, x2, z2) => Math.hypot(x1 - x2, z1 - z2) <= tolFt;

    const pool = sides.map((s) => ({ ...s }));
    const ordered = [pool.shift()];
    let endX = ordered[0].x2;
    let endZ = ordered[0].z2;

    while (pool.length > 0) {
        let idx = -1;
        let reversed = false;
        for (let i = 0; i < pool.length; i++) {
            const s = pool[i];
            if (near(endX, endZ, s.x1, s.z1)) {
                idx = i;
                break;
            }
            if (near(endX, endZ, s.x2, s.z2)) {
                idx = i;
                reversed = true;
                break;
            }
        }
        if (idx < 0) break;
        let next = pool.splice(idx, 1)[0];
        if (reversed) next = swapSide(next);
        ordered.push(next);
        endX = next.x2;
        endZ = next.z2;
    }

    return ordered.length >= 2 ? ordered : sides;
};

export const segmentToSideDraft = (geom, centerPx, feetPerPixel) => {

    const p1 = pxToLocalFt(geom.x1, geom.y1, centerPx, feetPerPixel);

    const p2 = pxToLocalFt(geom.x2, geom.y2, centerPx, feetPerPixel);



    if (geom.kind !== "arc" || geom.curveX == null || geom.curveY == null) {

        return {

            x1: p1.x, y1: 0, z1: p1.z,

            x2: p2.x, y2: 0, z2: p2.z,

            curveX: 0, curveY: 0, curveZ: 0,

            curveRadius: 0,

            isInner: false,

            isSiteRender: true,

        };

    }



    const circle = circleFromThreePoints(geom.x1, geom.y1, geom.x2, geom.y2, geom.curveX, geom.curveY);

    if (!circle) {

        return {

            x1: p1.x, y1: 0, z1: p1.z,

            x2: p2.x, y2: 0, z2: p2.z,

            curveX: 0, curveY: 0, curveZ: 0,

            curveRadius: 0,

            isInner: false,

            isSiteRender: true,

        };

    }



    const pc = pxToLocalFt(circle.cx, circle.cy, centerPx, feetPerPixel);

    return {

        x1: p1.x, y1: 0, z1: p1.z,

        x2: p2.x, y2: 0, z2: p2.z,

        curveX: pc.x, curveY: 0, curveZ: pc.z,

        curveRadius: round1(circle.r * feetPerPixel),

        isInner: !!geom.isInner,

        isSiteRender: true,

    };

};



/** Subdivide long straight edges so drawSite extrude matches curved layout preview. */
export const densifyPolyFt = (polyFt, maxEdgeFt = 1.5) => {
    if (!polyFt?.length) return polyFt;
    const out = [];
    for (let i = 0; i < polyFt.length; i++) {
        const a = polyFt[i];
        const b = polyFt[(i + 1) % polyFt.length];
        out.push(a);
        const len = Math.hypot(b.x - a.x, b.z - a.z);
        const steps = Math.max(1, Math.ceil(len / maxEdgeFt));
        for (let s = 1; s < steps; s++) {
            const t = s / steps;
            out.push({
                x: a.x + (b.x - a.x) * t,
                z: a.z + (b.z - a.z) * t,
            });
        }
    }
    return out;
};

const pushPolyPoint = (pts, x, z, tol = 0.08) => {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    const last = pts[pts.length - 1];
    if (last && Math.hypot(last.x - x, last.z - z) < tol) return;
    pts.push({ x: round1(x), z: round1(z) });
};

/** Sample a layout side arc in bin-local feet (matches SticeArcWall sweep). */
const sampleSideArcPoints = (side, steps = 12) => {
    const r = side.curveRadius;
    if (!(r > 0)) {
        return [
            { x: side.x1, z: side.z1 },
            { x: side.x2, z: side.z2 },
        ];
    }
    const cx = side.curveX ?? 0;
    const cz = side.curveZ ?? 0;
    let start = Math.atan2(side.z1 - cz, side.x1 - cx);
    let end = Math.atan2(side.z2 - cz, side.x2 - cx);
    let delta = end - start;
    if (!side.isInner) {
        if (delta <= 0) delta += 2 * Math.PI;
    } else if (delta >= 0) {
        delta -= 2 * Math.PI;
    }
    const n = Math.max(6, Math.ceil(Math.abs(delta) * r * 0.35));
    const pts = [];
    for (let i = 0; i <= n; i++) {
        const t = start + (delta * i) / n;
        pts.push({
            x: cx + r * Math.cos(t),
            z: cz + r * Math.sin(t),
        });
    }
    return pts;
};

/**
 * Walk chained wall / user-arc / bin-arc sides into a closed polygon (bin-local feet).
 */
export const buildPolygonFtFromGeometryBoundary = (
    stice,
    face,
    markers,
    segmentList,
    centerPx,
    feetPerPixel
) => {
    const binArcKeys = face?.binArcKeys || stice?.binArcKeys || [];
    const hasSegments = (stice?.segmentIds?.length || stice?.segments?.length) > 0;
    const hasBinArcs = binArcKeys.length > 0;
    if (!hasSegments && !hasBinArcs) return [];

    let ordered = (stice?.segments || []).map((s) => ({ segment: s, reversed: false }));
    if (stice?.segmentIds?.length) {
        ordered = orderFaceSegmentsLoop(stice.segmentIds, segmentList, markers);
    }

    const sidePieces = [];
    ordered.forEach(({ segment, reversed }) => {
        const g = resolveSegmentGeometry(segment, markers, segmentList);
        if (!g) return;
        const geom = reversed ? flipSegmentGeometry(g) : g;
        sidePieces.push(segmentToSideDraft(geom, centerPx, feetPerPixel));
    });
    binArcKeys.forEach((key) => {
        const arcSide = binArcKeyToSideDraft(
            key,
            markers,
            centerPx,
            feetPerPixel,
            null
        );
        if (arcSide) sidePieces.push(arcSide);
    });
    if (!sidePieces.length) return [];

    const chainTol = Math.max(0.75, (feetPerPixel || 0.1) * 6);
    const chained = chainSideDraftsByEndpoints(sidePieces, chainTol);
    return polygonFtFromChainedSides(chained);
};

/**
 * Closed outline for drawSite — enclosure polygon and/or sampled walls, arcs, bin legs.
 */
export const resolveSticePolygonFt = (
    stice,
    face,
    markers,
    segmentList,
    centerPx,
    feetPerPixel
) => {
    const previewPolygonFt = (face?.polygon || []).map((p) => ({
        x: round1((p.x - centerPx.x) * feetPerPixel),
        z: round1((p.y - centerPx.y) * feetPerPixel),
    }));

    const hasSegments = (stice?.segmentIds?.length || stice?.segments?.length) > 0;
    const hasBinArcs = (face?.binArcKeys || stice?.binArcKeys || []).length > 0;

    const geomPoly = (hasSegments || hasBinArcs)
        ? buildPolygonFtFromGeometryBoundary(
              stice,
              face,
              markers,
              segmentList,
              centerPx,
              feetPerPixel
          )
        : [];

    let polygonFt = [];
    if (!hasSegments && hasBinArcs && previewPolygonFt.length >= 3) {
        polygonFt = previewPolygonFt;
    } else if (hasSegments) {
        if (previewPolygonFt.length >= 3 && previewPolygonFt.length >= geomPoly.length) {
            polygonFt = previewPolygonFt;
        } else if (geomPoly.length >= 3) {
            polygonFt = geomPoly;
        } else if (previewPolygonFt.length >= 3) {
            polygonFt = previewPolygonFt;
        }
    } else if (previewPolygonFt.length >= 3) {
        polygonFt = previewPolygonFt;
    } else if (geomPoly.length >= 3) {
        polygonFt = geomPoly;
    }

    return densifyPolyFt(polygonFt);
};

/** Closed stice outline → straight layout sides (bin-local feet) for drawSite(). */
export const sidesFromPolygonFt = (polygonFt) => {
    const pts = (polygonFt || []).filter(
        (p) => typeof p.x === "number" && Number.isFinite(p.x) && typeof p.z === "number" && Number.isFinite(p.z)
    );
    if (pts.length < 3) return [];
    const sides = [];
    for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        sides.push({
            x1: a.x,
            y1: 0,
            z1: a.z,
            x2: b.x,
            y2: 0,
            z2: b.z,
            curveX: 0,
            curveY: 0,
            curveZ: 0,
            curveRadius: 0,
            isInner: false,
            isSiteRender: true,
        });
    }
    return sides;
};

/**
 * Persist round-bin perimeter walls/arcs (editor layoutSegments) as layoutSides so
 * Site Layout "Load" from Site 3D can restore stice↔bin connections.
 */
export const augmentDraftWithRoundBinPerimeterSides = (
    draft,
    markers,
    layoutSegments,
    siteCenterPx,
    feetPerPixel
) => {
    if (!draft?.groups?.length || !layoutSegments?.length || !markers?.length) {
        return draft;
    }
    const fpp = feetPerPixel > 0 ? feetPerPixel : PIXEL_TO_FEET_DEFAULT;
    const originX = siteCenterPx?.x ?? 0;
    const originY = siteCenterPx?.y ?? 0;

    draft.groups.forEach((group) => {
        const centerPx = {
            x: originX + (group.x ?? 0) / fpp,
            y: originY + (group.z ?? 0) / fpp,
        };
        (group.bins || []).forEach((binDraft) => {
            if (!binDraft.isRound) return;
            const marker = markers.find(
                (m) =>
                    m.binDataId != null &&
                    String(m.binDataId) === String(binDraft.binId)
            );
            if (!marker) return;

            const markerId = marker.id;
            const sidePieces = [...(binDraft.sides || [])];
            layoutSegments.forEach((seg) => {
                if (seg.markerId1 !== markerId && seg.markerId2 !== markerId) {
                    return;
                }
                const geom = resolveSegmentGeometry(
                    seg,
                    markers,
                    layoutSegments
                );
                if (!geom) return;
                sidePieces.push(segmentToSideDraft(geom, centerPx, fpp));
            });
            if (sidePieces.length > (binDraft.sides || []).length) {
                binDraft.sides = sidePieces;
            }
        });
    });
    return draft;
};

export const buildSticeBinDraft = (
    stice,
    groupCenterPx,
    feetPerPixel,
    bin,
    markers,
    allSegments,
    face = null,
    forPreview = false,
    siteBins = null,
    useEditorFootprint = false
) => {
    if (!bin) return null;

    const segmentList = allSegments || stice?.segments || [];
    const binArcKeys = face?.binArcKeys || stice?.binArcKeys || [];
    const hasSegments = (stice?.segmentIds?.length || stice?.segments?.length) > 0;
    const hasBinArcs = binArcKeys.length > 0;
    const faceId = String(face?.id ?? "");
    const hasSynthOutlinePolygon =
        (face?.polygon?.length ?? 0) >= 3 &&
        (faceId.startsWith("gap:tri:") ||
            faceId.startsWith("gap:hub:") ||
            faceId.startsWith("gap:throat:") ||
            faceId.startsWith("grid:bin:") ||
            faceId.startsWith("grid:wall:"));
    if (!hasSegments && !hasBinArcs && !hasSynthOutlinePolygon) return null;

    let ordered = (stice?.segments || []).map((s) => ({ segment: s, reversed: false }));
    if (stice?.segmentIds?.length) {
        ordered = orderFaceSegmentsLoop(stice.segmentIds, segmentList, markers);
    }

    const centerPx =
        face?.centroid ||
        (ordered.length
            ? sticeCentroid(
                  ordered.map((o) => o.segment),
                  markers,
                  segmentList
              )
            : { x: 0, y: 0 });

    const previewPolygonFt = (face?.polygon || []).map((p) => ({
        x: round1((p.x - centerPx.x) * feetPerPixel),
        z: round1((p.y - centerPx.y) * feetPerPixel),
    }));

    const siteBinsList = siteBins || [];
    if (
        !forPreview &&
        sticeCenterInsideCatalogBin(
            centerPx,
            groupCenterPx,
            markers,
            siteBinsList,
            feetPerPixel,
            face,
            useEditorFootprint
        )
    ) {
        return null;
    }

    const chained = buildSticeSidesForLayoutTables(
        stice,
        face,
        markers,
        segmentList,
        centerPx,
        feetPerPixel,
        siteBinsList,
        useEditorFootprint
    );

    let pieceCount = 0;
    ordered.forEach(() => {
        pieceCount += 1;
    });
    pieceCount += binArcKeys.length;

    const editorOutlineFt =
        previewPolygonFt.length >= 3 ? densifyPolyFt(previewPolygonFt, 1.25) : [];

    // Preview / editor-footprint Site 3D: walls must match the 2D face polygon.
    // Re-chaining walls+bin-arcs into layoutSides often drifts from the drawn
    // outline (Guntersville-style clusters) and skews SticeArcWall in 3D.
    let sides = chained;
    if (useEditorFootprint && editorOutlineFt.length >= 3) {
        sides = sidesFromPolygonFt(editorOutlineFt);
    } else if (
        (hasSynthOutlinePolygon || faceId.startsWith("grid:wall:")) &&
        editorOutlineFt.length >= 3
    ) {
        sides = sidesFromPolygonFt(editorOutlineFt);
    } else if (sides.length < 3 && editorOutlineFt.length >= 3) {
        sides = sidesFromPolygonFt(editorOutlineFt);
    }
    if (sides.length < 3) return null;

    const outlineFt =
        editorOutlineFt.length >= 3
            ? editorOutlineFt
            : densifyPolyFt(polygonFtFromChainedSides(chained), 1.25);
    if (outlineFt.length < 3) return null;

    const loopClosed =
        hasSynthOutlinePolygon ||
        faceId.startsWith("grid:wall:") ||
        useEditorFootprint ||
        (stice?.segmentIds?.length
            ? faceSegmentLoopIsClosed(ordered, markers, segmentList)
            : sides.length >= 3);



    let minX = Infinity;

    let maxX = -Infinity;

    let minZ = Infinity;

    let maxZ = -Infinity;

    sides.forEach((s) => {

        [ { x: s.x1, z: s.z1 }, { x: s.x2, z: s.z2 } ].forEach((p) => {

            minX = Math.min(minX, p.x);

            maxX = Math.max(maxX, p.x);

            minZ = Math.min(minZ, p.z);

            maxZ = Math.max(maxZ, p.z);

        });

    });



    const width = isFinite(maxX - minX) ? Math.max(maxX - minX, 1) : FALLBACK_BIN_SIZE;

    const length = isFinite(maxZ - minZ) ? Math.max(maxZ - minZ, 1) : FALLBACK_BIN_SIZE;

    const gx = groupCenterPx.centerX ?? groupCenterPx.x ?? 0;

    const gy = groupCenterPx.centerY ?? groupCenterPx.y ?? 0;

    const dx = (centerPx.x - gx) * feetPerPixel;

    const dz = (centerPx.y - gy) * feetPerPixel;



    return {

        binId: parseInt(bin.binDataId ?? bin.binId, 10) || 0,

        binName: String(bin.binName ?? ""),

        groupOrder: stice.groupOrder ?? 0,

        width: round1(width),

        length: round1(length),

        height: bin.height || 0,

        rotation: (() => {
            // Editor footprint already has yaw baked into the polygon vertices.
            if (forPreview || useEditorFootprint) return 0;
            const fromSides = flatRotationDegFromSidePieces(sides);
            if (fromSides != null && Number.isFinite(fromSides)) {
                return fromSides;
            }
            const editorRot = flatRotationDegFromEditor(
                segmentList,
                face
            );
            if (editorRot != null && Number.isFinite(editorRot)) {
                return editorRot;
            }
            return bin.degreesToNorth || 0;
        })(),

        isRound: false,

        x: round1(dx),

        y: 0,

        z: round1(dz),

        cx: null,

        cy: null,

        cz: null,

        sides,
        loopClosed,
        previewPolygonFt: outlineFt,

    };

};



const FALLBACK_BIN_SIZE = 30;



const sameSnapHighlight = (a, b) => {
    if (!a || !b) return false;
    if (a.kind === "segment-mid" && b.kind === "segment-mid") {
        return (
            a.segmentId === b.segmentId &&
            (a.t ?? 0.5) === (b.t ?? 0.5)
        );
    }
    if (a.kind === "segment-end" && b.kind === "segment-end") {
        return a.segmentId === b.segmentId && a.end === b.end;
    }
    return (
        a.markerId === b.markerId &&
        a.angle != null &&
        b.angle != null &&
        Math.abs(a.angle - b.angle) < 0.05
    );
};

/** Highlight snap points (bin edge, wall ends, wall midpoints). */
export const drawLayoutSnapPoints = (ctx, markers, segments, scaleX, scaleY, options = {}) => {
    const { hoverSnap = null, activeFrom = null } = options;
    const snaps = collectLayoutSnapPoints(markers, segments);
    snaps.forEach((sp) => {
        const cx = sp.x * scaleX;
        const cy = sp.y * scaleY;
        const isHover = sameSnapHighlight(hoverSnap, sp);
        const isFrom = sameSnapHighlight(activeFrom, sp);
        const isMid = sp.kind === "segment-mid";
        ctx.fillStyle = isFrom ? "#ffeb3b" : isHover ? "#fff" : isMid ? "rgba(0,188,212,0.9)" : "rgba(255,255,255,0.85)";
        ctx.strokeStyle = isFrom || isHover ? "#ff9800" : isMid ? "#00838f" : "#333";
        ctx.lineWidth = isFrom || isHover ? 2 : 1;
        ctx.beginPath();
        if (isMid) {
            const s = isFrom || isHover ? 7 : 5;
            ctx.rect(cx - s, cy - s, s * 2, s * 2);
        } else {
            ctx.arc(cx, cy, isFrom || isHover ? 6 : 4, 0, Math.PI * 2);
        }
        ctx.fill();
        ctx.stroke();
    });
};

/** @deprecated use drawLayoutSnapPoints */
export const drawBinSnapPoints = (ctx, markers, scaleX, scaleY, options = {}) =>
    drawLayoutSnapPoints(ctx, markers, [], scaleX, scaleY, options);



/** Draw stice walls/arcs on the layout canvas (image space scaled to canvas). */

export const drawSticeSegments = (ctx, stices, markers, scaleX, scaleY, options = {}) => {

    const {

        activeSticeId = null,

        selectedSegmentId = null,
        selectedSegmentIds = null,

        previewSegment = null,

        showEndpointHandles = true,

        colors = ["#e91e63", "#9c27b0", "#00bcd4", "#ff5722"],

    } = options;



    const drawSeg = (seg, color, lineWidth, dashed = false, floating = false) => {

        const x1 = seg.x1 * scaleX;

        const y1 = seg.y1 * scaleY;

        const x2 = seg.x2 * scaleX;

        const y2 = seg.y2 * scaleY;

        ctx.strokeStyle = floating ? "#e65100" : color;

        ctx.lineWidth = lineWidth;

        ctx.setLineDash(floating || dashed ? [6, 4] : []);

        ctx.beginPath();

        if (seg.kind === "arc" && seg.curveX != null && seg.curveY != null) {

            const circle = circleFromThreePoints(seg.x1, seg.y1, seg.x2, seg.y2, seg.curveX, seg.curveY);

            if (circle) {

                const start = Math.atan2(seg.y1 - circle.cy, seg.x1 - circle.cx);

                const end = Math.atan2(seg.y2 - circle.cy, seg.x2 - circle.cx);

                ctx.arc(

                    circle.cx * scaleX,

                    circle.cy * scaleY,

                    circle.r * Math.min(scaleX, scaleY),

                    start,

                    end,

                    !seg.isInner

                );

            } else {

                ctx.moveTo(x1, y1);

                ctx.lineTo(x2, y2);

            }

        } else {

            ctx.moveTo(x1, y1);

            ctx.lineTo(x2, y2);

        }

        ctx.stroke();

        ctx.setLineDash([]);

        if (showEndpointHandles) {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(x1, y1, 4, 0, Math.PI * 2);
            ctx.arc(x2, y2, 4, 0, Math.PI * 2);
            ctx.fill();
        }

    };



    const allSegments = stices.flatMap((s) => s.segments || []);

    stices.forEach((stice, si) => {

        const color = colors[si % colors.length];

        const isActive = stice.id === activeSticeId;

        (stice.segments || []).forEach((raw) => {

            const floating = isFloatingSegment(raw, markers, allSegments);
            const seg = resolveSegmentGeometry(raw, markers, allSegments);
            const drawGeom =
                seg ||
                (floating && raw.x1 != null && raw.x2 != null
                    ? {
                          kind: raw.kind || "wall",
                          x1: raw.x1,
                          y1: raw.y1,
                          x2: raw.x2,
                          y2: raw.y2,
                          curveX: raw.bulgeX,
                          curveY: raw.bulgeY,
                          isInner: !!raw.isInner,
                      }
                    : null);

            if (!drawGeom) return;

            const selected =
                raw.id === selectedSegmentId ||
                (selectedSegmentIds && selectedSegmentIds.includes(raw.id));

            drawSeg(drawGeom, color, selected ? 4 : isActive ? 3 : 2, false, floating);

        });

    });



    if (previewSegment) {

        drawSeg(previewSegment, "#ffeb3b", 2, true);

        if (previewSegment.kind === "arc" && previewSegment.curveX != null) {

            ctx.fillStyle = "#ffeb3b";

            ctx.beginPath();

            ctx.arc(

                previewSegment.curveX * scaleX,

                previewSegment.curveY * scaleY,

                5,

                0,

                Math.PI * 2

            );

            ctx.fill();

        }

    }

};



const distToSegment = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-6) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
};

/** Sweep (rad) from start→end along the arc that passes through the bulge/curve point. */
const arcSweepThroughBulge = (start, end, bulge) => {
    const TAU = 2 * Math.PI;
    const norm = (a) => ((a % TAU) + TAU) % TAU;
    const s = norm(start);
    const e = norm(end);
    const b = norm(bulge);
    const ccw = (e - s + TAU) % TAU;
    const bFromS = (b - s + TAU) % TAU;
    if (bFromS <= ccw) {
        return ccw > Math.PI ? ccw - TAU : ccw;
    }
    const cw = ccw - TAU;
    return cw < -Math.PI ? cw + TAU : cw;
};

/** Distance from image-space point to arc stroke (matches drawn bulge arc). */
const distToArcImage = (seg, px, py) => {
    const curveX = seg.curveX ?? seg.bulgeX;
    const curveY = seg.curveY ?? seg.bulgeY;
    if (curveX == null || curveY == null) {
        return distToSegment(px, py, seg.x1, seg.y1, seg.x2, seg.y2);
    }
    const circle = circleFromThreePoints(seg.x1, seg.y1, seg.x2, seg.y2, curveX, curveY);
    if (!circle) return distToSegment(px, py, seg.x1, seg.y1, seg.x2, seg.y2);

    const start = Math.atan2(seg.y1 - circle.cy, seg.x1 - circle.cx);
    const end = Math.atan2(seg.y2 - circle.cy, seg.x2 - circle.cx);
    const mid = Math.atan2(curveY - circle.cy, curveX - circle.cx);
    const delta = arcSweepThroughBulge(start, end, mid);
    const steps = 32;
    let best = Infinity;
    for (let i = 0; i < steps; i++) {
        const t1 = start + (delta * i) / steps;
        const t2 = start + (delta * (i + 1)) / steps;
        const px1 = circle.cx + circle.r * Math.cos(t1);
        const py1 = circle.cy + circle.r * Math.sin(t1);
        const px2 = circle.cx + circle.r * Math.cos(t2);
        const py2 = circle.cy + circle.r * Math.sin(t2);
        best = Math.min(best, distToSegment(px, py, px1, py1, px2, py2));
    }
    return best;
};

const hitTestSegmentGeomImage = (seg, imageX, imageY) => {
    if (!seg) return Infinity;
    const isArc =
        seg.kind === "arc" ||
        (seg.curveX != null && seg.curveY != null) ||
        (seg.bulgeX != null && seg.bulgeY != null);
    if (isArc) {
        return distToArcImage(seg, imageX, imageY);
    }
    return distToSegment(imageX, imageY, seg.x1, seg.y1, seg.x2, seg.y2);
};

/** Hit-test wall/arc endpoint or midpoint snap (square/circle handles), canvas px. */
export const findSegmentSnapAtCanvas = (
    markers,
    segments,
    canvasX,
    canvasY,
    scaleX,
    scaleY,
    tolCanvasPx = 16
) => {
    const scaleMin = Math.min(scaleX, scaleY);
    const imageX = canvasX / scaleX;
    const imageY = canvasY / scaleY;
    const snap = snapImageToLayoutPoint(
        imageX,
        imageY,
        markers,
        segments,
        scaleMin,
        tolCanvasPx
    );
    if (!snap) return null;
    if (snap.kind === "segment-mid" || snap.kind === "segment-end") return snap;
    return null;
};

const segmentHitTestGeom = (raw, markers, segments) => {
    const resolved = resolveSegmentGeometry(raw, markers, segments);
    if (resolved) return resolved;
    if (raw.x1 == null || raw.x2 == null) return null;
    return {
        id: raw.id,
        kind: raw.kind || "wall",
        x1: raw.x1,
        y1: raw.y1,
        x2: raw.x2,
        y2: raw.y2,
        curveX: raw.curveX ?? raw.bulgeX,
        curveY: raw.curveY ?? raw.bulgeY,
        isInner: !!raw.isInner,
    };
};

/** Hit-test segment in canvas space (tolerance px), including legacy floating lines. */
export const findSegmentAtCanvas = (segments, markers, canvasX, canvasY, scaleX, scaleY, tol = 10) => {
    const scaleMin = Math.min(scaleX, scaleY) || 1;
    const imageX = canvasX / scaleX;
    const imageY = canvasY / scaleY;
    const tolImage = tol / scaleMin;
    let best = null;
    let bestDist = Infinity;
    (segments || []).forEach((raw) => {
        const seg = segmentHitTestGeom(raw, markers, segments);
        if (!seg) return;
        const d = hitTestSegmentGeomImage(seg, imageX, imageY);
        if (!Number.isFinite(d) || d === Infinity) return;
        const isArc =
            raw.kind === "arc" ||
            raw.bulgeX != null ||
            raw.curveX != null ||
            seg.kind === "arc";
        const hitTol = isArc ? Math.max(tolImage, 14) : tolImage;
        if (d < hitTol && d < bestDist) {
            bestDist = d;
            best = raw;
        }
    });
    return best;
};

export const snapsAreSameEndpoint = (a, b) => {
    if (!a || !b) return false;
    if (a.kind === "segment-mid" && b.kind === "segment-mid") {
        return (
            a.segmentId === b.segmentId &&
            (a.t ?? 0.5) === (b.t ?? 0.5)
        );
    }
    if (a.kind === "segment-mid" || b.kind === "segment-mid") return false;
    if (a.kind === "segment-end" && b.kind === "segment-end") {
        return a.segmentId === b.segmentId && a.end === b.end;
    }
    return a.markerId === b.markerId && Math.abs((a.angle ?? 0) - (b.angle ?? 0)) < 0.04;
};

export const snapsAllowWall = (fromSnap, toSnap) => {
    if (!fromSnap || !toSnap) return false;
    if (snapsAreSameEndpoint(fromSnap, toSnap)) return false;
    if (fromSnap.kind === "segment-mid" || toSnap.kind === "segment-mid") return true;
    // Two distinct snap points on the same bin (different angles) — needed to close flat areas with an arc + bin edge.
    if (
        fromSnap.markerId &&
        toSnap.markerId &&
        fromSnap.markerId === toSnap.markerId &&
        fromSnap.angle != null &&
        toSnap.angle != null
    ) {
        return Math.abs(fromSnap.angle - toSnap.angle) > 0.05;
    }
    return true;
};

/** Bin edge, wall end/mid, or explicit image coordinate. */
export const isLayoutSnapPoint = (pt) =>
    !!pt &&
    (pt.kind === "segment-end" ||
        pt.kind === "segment-mid" ||
        (pt.markerId != null && pt.angle != null));

const layoutPointXY = (pt, end, markers, segments) => {
    if (isLayoutSnapPoint(pt)) {
        const draft = { id: "_ep", kind: "wall" };
        applySnapToEnd(draft, end, pt);
        const g = resolveSegmentGeometry(draft, markers, segments);
        if (g) {
            return end === 1
                ? { x: g.x1, y: g.y1 }
                : { x: g.x2, y: g.y2 };
        }
    }
    return { x: pt.x, y: pt.y };
};

/** Wall between snap and/or free image points. */
export const layoutPointsAllowWall = (fromPt, toPt, minLen = 2) => {
    if (!fromPt || !toPt) return false;
    if (Math.hypot(fromPt.x - toPt.x, fromPt.y - toPt.y) < minLen) {
        return false;
    }
    if (isLayoutSnapPoint(fromPt) && isLayoutSnapPoint(toPt)) {
        return snapsAllowWall(fromPt, toPt);
    }
    return true;
};

/** Wall or arc between layout snap points and/or free image coordinates. */
export const createSegmentBetweenLayoutPoints = (
    fromPt,
    toPt,
    kind,
    bulgePoint,
    markers,
    segments
) => {
    if (!fromPt || !toPt) return null;
    if (isLayoutSnapPoint(fromPt) && isLayoutSnapPoint(toPt)) {
        return createSegmentBetweenSnaps(fromPt, toPt, kind, bulgePoint);
    }
    const seg = { id: newSegmentId(), kind, isInner: false };
    if (isLayoutSnapPoint(fromPt)) applySnapToEnd(seg, 1, fromPt);
    if (isLayoutSnapPoint(toPt)) applySnapToEnd(seg, 2, toPt);
    const p1 = layoutPointXY(fromPt, 1, markers, segments);
    const p2 = layoutPointXY(toPt, 2, markers, segments);
    seg.x1 = p1.x;
    seg.y1 = p1.y;
    seg.x2 = p2.x;
    seg.y2 = p2.y;
    if (kind === "arc" && bulgePoint) {
        seg.bulgeX = bulgePoint.x;
        seg.bulgeY = bulgePoint.y;
        seg.isInner = arcIsInnerFromBulge(
            p1.x,
            p1.y,
            p2.x,
            p2.y,
            bulgePoint.x,
            bulgePoint.y
        );
    }
    return seg;
};

/** Gap between a new placement and existing layout geometry (image px). */
export const PLACEMENT_CLEARANCE_PX = 4;

const pointInPolygonLocal = (x, y, poly) => {
    if (!poly?.length) return false;
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x;
        const yi = poly[i].y;
        const xj = poly[j].x;
        const yj = poly[j].y;
        const intersect =
            yi > y !== yj > y &&
            x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
};

const aabbOverlap = (a, b, pad = 0) =>
    !(
        a.maxX + pad < b.minX - pad ||
        a.minX - pad > b.maxX + pad ||
        a.maxY + pad < b.minY - pad ||
        a.minY - pad > b.maxY + pad
    );

const circleCircleOverlap = (x1, y1, r1, x2, y2, r2, pad = 0) =>
    Math.hypot(x1 - x2, y1 - y2) < r1 + r2 + pad;

const circleRectOverlap = (cx, cy, r, bounds, pad = 0) => {
    const closestX = Math.max(bounds.minX, Math.min(cx, bounds.maxX));
    const closestY = Math.max(bounds.minY, Math.min(cy, bounds.maxY));
    return Math.hypot(cx - closestX, cy - closestY) < r + pad;
};

const circleOverlapsPolygon = (cx, cy, r, poly, pad = 0) => {
    if (pointInPolygonLocal(cx, cy, poly)) return true;
    const steps = 12;
    for (let i = 0; i < steps; i++) {
        const a = (2 * Math.PI * i) / steps;
        const px = cx + (r + pad) * Math.cos(a);
        const py = cy + (r + pad) * Math.sin(a);
        if (pointInPolygonLocal(px, py, poly)) return true;
    }
    return false;
};

const rectOverlapsFacePolygon = (bounds, poly, pad = 0) => {
    if (!poly?.length) return false;
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = (bounds.minY + bounds.maxY) / 2;
    if (pointInPolygonLocal(cx, cy, poly)) return true;
    return poly.some((p) => pointInFlatRectBounds(p.x, p.y, bounds, pad));
};

const segmentBlocksRoundBin = (seg, markers, segments, cx, cy, radiusPx, pad) => {
    const geom = segmentHitTestGeom(seg, markers, segments);
    if (!geom) return false;
    return hitTestSegmentGeomImage(geom, cx, cy) < radiusPx + pad;
};

const segmentBlocksFlatRect = (seg, markers, segments, bounds, pad) => {
    const geom = segmentHitTestGeom(seg, markers, segments);
    if (!geom) return false;
    const halfDiag =
        Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) / 2 + pad;
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = (bounds.minY + bounds.maxY) / 2;
    if (hitTestSegmentGeomImage(geom, cx, cy) < halfDiag) return true;
    const corners = [
        { x: bounds.minX, y: bounds.minY },
        { x: bounds.maxX, y: bounds.minY },
        { x: bounds.maxX, y: bounds.maxY },
        { x: bounds.minX, y: bounds.maxY },
    ];
    return corners.some(
        (p) => hitTestSegmentGeomImage(geom, p.x, p.y) < pad + 3
    );
};

const eachFlatRectBounds = (segments, skipRectId, fn) => {
    const seen = new Set();
    (segments || []).forEach((seg) => {
        const rectId = seg.closedRectId;
        if (!rectId || rectId === skipRectId || seen.has(rectId)) return;
        seen.add(rectId);
        const bounds = getFlatRectBounds(segments, rectId);
        if (bounds) fn(bounds, rectId);
    });
};

/**
 * True when a new round bin at (cx, cy) would overlap bins, flats, walls/arcs, or areas.
 */
export const layoutPlacementBlockedForRoundBin = ({
    cx,
    cy,
    radiusPx,
    markers = [],
    segments = [],
    faces = [],
    excludeMarkerId = null,
    clearancePx = PLACEMENT_CLEARANCE_PX,
}) => {
    const r =
        radiusPx > 4 && Number.isFinite(radiusPx)
            ? radiusPx
            : DEFAULT_MARKER_RADIUS_PX;
    const pad = clearancePx;

    for (const m of markers) {
        if (excludeMarkerId && m.id === excludeMarkerId) continue;
        if (
            circleCircleOverlap(
                cx,
                cy,
                r,
                m.x,
                m.y,
                getMarkerRadiusImagePx(m),
                pad
            )
        ) {
            return true;
        }
    }

    let blocked = false;
    eachFlatRectBounds(segments, null, (bounds) => {
        if (!blocked && circleRectOverlap(cx, cy, r, bounds, pad)) {
            blocked = true;
        }
    });
    if (blocked) return true;

    for (const raw of segments || []) {
        if (raw.closedRectId) continue;
        if (segmentBlocksRoundBin(raw, markers, segments, cx, cy, r, pad)) {
            return true;
        }
    }

    for (const f of faces || []) {
        if (f.polygon?.length && circleOverlapsPolygon(cx, cy, r, f.polygon, pad)) {
            return true;
        }
    }

    return false;
};

/**
 * True when a new flat rectangle would overlap bins, other flats, walls/arcs, or areas.
 */
export const layoutPlacementBlockedForFlatRect = ({
    minX,
    minY,
    maxX,
    maxY,
    markers = [],
    segments = [],
    faces = [],
    excludeRectId = null,
    clearancePx = PLACEMENT_CLEARANCE_PX,
}) => {
    const bounds = {
        minX: Math.min(minX, maxX),
        minY: Math.min(minY, maxY),
        maxX: Math.max(minX, maxX),
        maxY: Math.max(minY, maxY),
    };
    const pad = clearancePx;

    for (const m of markers) {
        if (circleRectOverlap(m.x, m.y, getMarkerRadiusImagePx(m), bounds, pad)) {
            return true;
        }
    }

    let blocked = false;
    eachFlatRectBounds(segments, excludeRectId, (other) => {
        if (!blocked && aabbOverlap(bounds, other, pad)) {
            blocked = true;
        }
    });
    if (blocked) return true;

    for (const raw of segments || []) {
        if (raw.closedRectId === excludeRectId) continue;
        if (raw.closedRectId) continue;
        if (segmentBlocksFlatRect(raw, markers, segments, bounds, pad)) {
            return true;
        }
    }

    for (const f of faces || []) {
        if (f.polygon?.length && rectOverlapsFacePolygon(bounds, f.polygon, pad)) {
            return true;
        }
    }

    return false;
};


